// Configuration constant to control display of prices in line items
const DISPLAY_LINE_ITEM_PRICE = false;
const DISPLAY_LINE_ITEM_PRICE_DRAFT = false;
// Configuration constant to control bundle functionality
const USE_BUNDLES = false;

class ServerConfigurator extends HTMLElement {
  constructor() {
    super();
    this.selectedDrives = new Map();
    this.maxDriveSlots = 0;
    this.usedDriveSlots = 0;
    this.isFirstUpdate = true;
    
    // Add navigation listener with modern performance check
    window.addEventListener('pageshow', (event) => {
      if (event.persisted || (performance.getEntriesByType('navigation')[0]?.type === 'back_forward')) {
        this.resetConfigurator();
      }
    });
    
    this.init();
  }

  init() {
    this.setupTabs(); 
    this.setupModals();
    this.initializeInfoModals();
    this.setupInitialFilters();
    this.setupMemorySizeFilter();
    this.setupRelatedServerDropdown();
    this.setupCardSelectors();
    this.setupFilters();
    this.setupAddOns();
    this.setupWarrantyHandlers();
    this.updateTotalConsumption(); 
    this.setupDetailsAnimation();
    this.setupPriceUpdates();
    this.setupAddToCart();
    this.setupDriveSelections();
    this.setupModalProcessorSelection();
    this.setupDriverFilters();
    this.setupPreBuiltCustomization();
    this.setupPreBuiltSelection();
    this.setupNetworkCardDropdown();
    this.setupOsDropdowns();
    this.setupPreBuiltPrices();
    this.setupQuoteButtonObserver();
    this.setupRequestQuote();
  }

  setupQuoteButtonObserver() {
    // Create a MutationObserver to watch for the quote button
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          const quoteButton = document.querySelector('.omgrfq-btn-quotes');
          if (quoteButton && !quoteButton.dataset.listenerAdded) {
            quoteButton.dataset.listenerAdded = 'true';
            quoteButton.addEventListener('click', () => {
              // Wait for modal to be added to DOM
              setTimeout(() => {
                this.updateQuoteTextArea(); 
              }, 500);
            });
          }
        }
      });
    });

    // Start observing the document body
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  updateQuoteModalPrice() {
    const modal = document.querySelector('#app-quotes');
    if (!modal) return;

    // Get current configurator price
    const priceElement = document.querySelector('.product-summary-price');
    if (!priceElement) return;
    
    const currentPrice = priceElement.textContent.trim();

    // Update price in modal
    const modalPriceElements = modal.querySelectorAll('.omgrfq_product_price span, .omgrfq_total_price div, .ot-total');
    modalPriceElements.forEach(element => {
      element.textContent = currentPrice;
    });
  }

  updateQuoteTextArea() {
    setTimeout(() => {
      const textarea = document.querySelector('#omgrfq-form-product_0');
      if (!textarea) return;
    
      const properties = this.getConfigurationProperties();
      
      let configText = '';
      
      Object.entries(properties).forEach(([key, value]) => {
        if (key === '_total_price') return;
        configText += `${key}: ${value}\n`;
      });
    
      // Set the value
      textarea.value = configText;
      
      // Trigger input and change events to ensure the quote app detects the change
      textarea.dispatchEvent(new Event('input', { bubbles: true }));
      textarea.dispatchEvent(new Event('change', { bubbles: true }));
      
      // Force the quote app to recognize the change by focusing and blurring the textarea
      textarea.focus();
      textarea.blur();
    }, 500);
  }
  // Tab Related Methods for Pre-Built Section
  setupTabs() {
    const tabButtons = this.querySelectorAll('.configurator__tab-btn');
    
    tabButtons.forEach(button => {
      button.addEventListener('click', () => {
        // Remove active class from all buttons
        tabButtons.forEach(btn => btn.classList.remove('configurator__tab-btn--active'));
        
        // Add active class to clicked button
        button.classList.add('configurator__tab-btn--active');
        
        // Hide all content sections
        this.querySelectorAll('[data-tab-content]').forEach(content => {
          content.style.display = 'none';
        });
        
        // Show selected content
        const tabContent = this.querySelector(`[data-tab-content="${button.dataset.tab}"]`);
        if (tabContent) {
          tabContent.style.display = '';
        }
      });
    });
  }

  setupRelatedServerDropdown() {
    document.querySelector('select[name="related-server-selection"]')?.addEventListener('change', function() {
      const selectedUrl = this.value;
      if (selectedUrl) {
        window.location.href = selectedUrl;
      }
    });
  }

  // Modal Related Methods
  setupModals() {
    this.initializeDriverModal();
    this.initializeProcessorModal();

    document.querySelectorAll('.configurator__modal-close').forEach(closeBtn => {
      closeBtn.addEventListener('click', (e) => {
        const modal = e.target.closest('.configurator__modal');
        this.closeModal(modal);
      });
    });

    document.querySelectorAll('.configurator__modal').forEach(modal => {
      modal.addEventListener('click', (e) => {
        if (e.target.classList.contains('configurator__modal')) {
          this.closeModal(e.target);
        }
      });
    });
  }

  initializeInfoModals() {
    this.querySelectorAll('.configurator__info-modal-button').forEach(button => {
      button.addEventListener('click', () => {
        const modalType = button.dataset.modalType;
        const modal = document.querySelector(`.configurator__info-modal[data-modal-type="${modalType}"]`);
        if (modal) {
          this.openModal(modal);
        }
      });
    });
  }

  openModal(modal) {
    if (modal) {
      modal.style.display = 'flex';
      document.body.style.overflow = 'hidden';

      requestAnimationFrame(() => {
        modal.classList.add('configurator__modal--visible');
      });
    }
  }

  closeModal(modal) {
    if (modal) {
      modal.classList.remove('configurator__modal--visible');

      modal.addEventListener('transitionend', function handler(e) {

        if (e.target === modal && e.propertyName === 'opacity') {
          modal.style.display = 'none';
          document.body.style.overflow = '';
          modal.removeEventListener('transitionend', handler);
        }
      });
    }
  }

  // ************* Processor Related Methods *************

  initializeProcessorModal() {
    this.querySelector('.configurator__processor-modal-show-btn[data-modal-type="processor"]')?.addEventListener('click', () => {
      const modal = document.querySelector('.configurator__processor-modal');
      if (modal) {
        // Get current active filters
        const section = this.querySelector('.configurator__section');
        const activeFilters = this.getProcessorActiveFilters(section);
        
        // Apply filters to modal items
        modal.querySelectorAll('.configurator__processor-list-item').forEach(item => {
          const matchesFilters = Object.entries(activeFilters).every(([field, value]) => {
            return item.dataset[field] === value;
          });
          item.style.display = matchesFilters ? '' : 'none';
        });

        this.openModal(modal);
      }
    });

    if (document.querySelector('.configurator__processor-modal')) {
      this.setupProcessorModalFilters();
    }
  }

  setupProcessorModalFilters() {
    // Setup filters
    this.setupProcessorModalFilterSelect('core-count', 'processor_core_count', 'Cores');
    this.setupProcessorModalFilterSelect('cache', 'processor_cache', 'Cache');
    this.setupProcessorModalFilterSelect('clock-speed', 'processor_speed', 'Clock Speeds');
    this.setupProcessorModalFilterSelect('thread-count', 'processor_thread_count', 'Threads');

    // Setup sort functionality
    const sortSelect = document.querySelector('.configurator__processor-modal .configurator__filter-select[data-filter-type="sort"]');
    if (sortSelect) {
      sortSelect.addEventListener('change', () => {
        this.sortProcessors(sortSelect.value);
      });
    }

    // Add filter change listeners
    document.querySelectorAll('.configurator__processor-modal .configurator__filter-select').forEach(select => {
      select.addEventListener('change', () => {
        this.filterModalProcessors();
      });
    });
  }

  setupProcessorModalFilterSelect(filterType, metafieldKey, defaultText) {
    const container = document.getElementById(`${filterType}-filter-container`);
    if (!container) return;

    // Collect all values
    const items = document.querySelectorAll('.configurator__processor-list-item');
    const values = new Set();
    items.forEach(item => {
      const value = item.querySelector(`.configurator__modal-processor-hide-on-mobile:nth-child(${this.getProcessorMetafieldIndex(metafieldKey)})`).textContent.trim();
      if (value) values.add(value);
    });

    // Create select element
    const select = document.createElement('select');
    select.className = 'configurator__filter-select';
    select.dataset.filterType = filterType;

    // Add default option
    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = defaultText;
    select.appendChild(defaultOption);

    // Add sorted options
    Array.from(values)
      .sort((a, b) => this.compareValues(a, b, metafieldKey))
      .forEach(value => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        select.appendChild(option);
      });

    container.appendChild(select);
  }

  filterModalProcessors() {
    const coreCountFilter = document.querySelector('#core-count-filter-container select').value;
    const cacheFilter = document.querySelector('#cache-filter-container select').value;
    const speedFilter = document.querySelector('#clock-speed-filter-container select').value;
    const threadFilter = document.querySelector('#thread-count-filter-container select').value;
    
    const items = document.querySelectorAll('.configurator__processor-list-item');
    
    items.forEach(item => {
      const coreCount = item.querySelector('.configurator__modal-processor-hide-on-mobile:nth-child(4)').textContent.trim();
      const cache = item.querySelector('.configurator__modal-processor-hide-on-mobile:nth-child(2)').textContent.trim();
      const speed = item.querySelector('.configurator__modal-processor-hide-on-mobile:nth-child(3)').textContent.trim();
      const threadCount = item.querySelector('.configurator__modal-processor-hide-on-mobile:nth-child(5)').textContent.trim();
      
      const matchesCore = !coreCountFilter || coreCount === coreCountFilter;
      const matchesCache = !cacheFilter || cache === cacheFilter;
      const matchesSpeed = !speedFilter || speed === speedFilter;
      const matchesThread = !threadFilter || threadCount === threadFilter;
      
      item.style.display = matchesCore && matchesCache && matchesSpeed && matchesThread ? '' : 'none';
    });
  }

  getProcessorsInCategory(modal, activeFilters) {
    if (!modal) return 0;
    
    const modalItems = modal.querySelectorAll('.configurator__processor-list-item');
    return Array.from(modalItems).filter(item => {
      return Object.entries(activeFilters).every(([field, value]) => {
        const itemValue = item.dataset[field];
        return itemValue === value;
      });
    }).length;
  }

  setupModalProcessorSelection() {
    document.querySelectorAll('.configurator__processor-select-btn').forEach(button => {
      button.addEventListener('click', (e) => {
        const modalItem = e.target.closest('.configurator__processor-list-item');
        const processorsList = this.querySelector('#configurator-processors');
        const selectedProductId = modalItem.dataset.productId;
        
        // Check if card already exists
        const existingCard = processorsList.querySelector(`input[value="${selectedProductId}"]`)?.closest('.configurator__card');
        
        if (existingCard) {
          // Select existing card
          const input = existingCard.querySelector('input[type="radio"]');
          input.checked = true;
          
          // Update selected states
          processorsList.querySelectorAll('.configurator__card').forEach(card => {
            card.classList.remove('configurator__card--selected');
          });
          existingCard.classList.add('configurator__card--selected');
          
          // Update prices
          const section = processorsList.closest('.configurator__section');
          this.updatePriceDifferences(section);
        } else {
          // Create and add new card
          const contentWrapper = processorsList.closest('.configurator__content-wrapper');
          const currentHeight = contentWrapper.scrollHeight;
          
          const newCard = this.createProcessorCard({
            id: selectedProductId,
            variantId: modalItem.dataset.variantId,
            title: modalItem.querySelector('.configurator__item-title').textContent,
            price: modalItem.querySelector('.configurator__item-price').dataset.price,
            series: modalItem.dataset.filter_processor_serie,
            category: modalItem.dataset.filter_processor_category
          });

          // Unselect current selection
          processorsList.querySelectorAll('.configurator__card').forEach(card => {
            card.classList.remove('configurator__card--selected');
            card.querySelector('input[type="radio"]').checked = false;
          });

          // Add and select new card
          processorsList.appendChild(newCard);
          newCard.querySelector('input[type="radio"]').checked = true;
          newCard.classList.add('configurator__card--selected');

          // Handle height adjustment
          requestAnimationFrame(() => {
            const newHeight = contentWrapper.scrollHeight;
            contentWrapper.style.height = `${currentHeight}px`;
            
            requestAnimationFrame(() => {
              contentWrapper.style.height = `${newHeight}px`;
              contentWrapper.addEventListener('transitionend', function handler() {
                contentWrapper.style.height = 'auto';
                contentWrapper.removeEventListener('transitionend', handler);
              }, { once: true });
            });
          });

          // Update prices
          const section = processorsList.closest('.configurator__section');
          this.updatePriceDifferences(section);
        }

        // Close modal and trigger price update
        this.closeModal(document.querySelector('.configurator__processor-modal'));
        this.dispatchEvent(new CustomEvent('configuration-change'));
      });
    });
  }

  createProcessorCard(processor) {
    const card = document.createElement('li');
    card.className = 'configurator__card';
    
    // Add filter data attributes and modal flag
    card.dataset.filter_processor_serie = processor.series;
    card.dataset.filter_processor_category = processor.category;
    card.dataset.from_modal = 'true';
    
    card.innerHTML = `
      <label class="configurator__card-label">
        <input type="radio" 
               name="processor-selection" 
               value="${processor.id}" 
               data-variant-id="${processor.variantId}"
               class="configurator__selector" 
               data-filter_processor_serie="${processor.series}"
               data-filter_processor_category="${processor.category}"
               hidden>
        <div class="configurator__card-content">
          <div class="configurator__card-info">
            <h3 class="configurator__card-title">${processor.title}</h3>
          </div>
          <div class="configurator__card-selection">
            <p class="configurator__card-price" data-price="${processor.price}">
              +${this.formatMoney(processor.price)}
            </p>
            <div class="configurator__custom-radio"></div>
          </div>
        </div>
      </label>
    `;

    const input = card.querySelector('input[type="radio"]');
    input.addEventListener('change', (event) => {
      if (event.target.checked) {
        const section = card.closest('.configurator__section');
        const ul = card.closest('ul');
        
        ul.querySelectorAll('.configurator__card').forEach(otherCard => {
          otherCard.classList.remove('configurator__card--selected');
        });
        card.classList.add('configurator__card--selected');
        
        this.updatePriceDifferences(section);
        this.dispatchEvent(new CustomEvent('configuration-change'));
      }
    });

    return card;
  }

  getProcessorMetafieldIndex(metafieldKey) {
    const indexes = {
      'processor_cache': 2,
      'processor_speed': 3,
      'processor_core_count': 4,
      'processor_thread_count': 5
    };
    return indexes[metafieldKey] || 1;
  }

  sortProcessors(sortValue) {
    this.sortItems(
      sortValue, 
      '.configurator__processor-list', 
      '.configurator__item-price', 
      true
    );
  }

  updateProcessorModalButtonVisibility(section, totalInCategory) {
    const modalButton = section.querySelector('.configurator__processor-modal-show-btn');
    
    if (modalButton) {
      if (totalInCategory > 5) {
        modalButton.style.display = '';
        modalButton.textContent = `Select From All ${totalInCategory} Options`;
      } else {
        modalButton.style.display = 'none';
      }
    }
  }

  updateProcessorVisibleCards(section) {
    const activeFilters = this.getProcessorActiveFilters(section);
    const processorCards = section.querySelectorAll('#configurator-processors .configurator__card');
     // Update processor cards
     if (processorCards.length > 0) {
      let cardsInCurrentCategory = 0;
      processorCards.forEach(card => {
          const matchesFilters = this.cardMatchesFilters(card, activeFilters);
          card.style.display = matchesFilters ? '' : 'none';
          if (matchesFilters) cardsInCurrentCategory++;
      });

      // Handle processor modal button visibility
      const modal = document.querySelector('.configurator__processor-modal');
      const totalInCategory = this.getProcessorsInCategory(modal, activeFilters);
      this.updateProcessorModalButtonVisibility(section, totalInCategory);
    }
  }

  getProcessorActiveFilters(section) {
    const filters = {};
    section.querySelectorAll('.configurator__filter-btn--active').forEach(button => {
        const field = button.dataset.filterField;
        const value = button.dataset.filterValue;
        if (field && value) {
            filters[field] = value;
        }
    });

    return filters;
  }

  // ************* Driver Related Methods *************

  initializeDriverModal() {
    this.querySelector('.configurator__driver-modal-show-btn[data-modal-type="driver"]')?.addEventListener('click', () => {
      const modal = document.querySelector('.configurator__driver-modal');
      if (modal) {
        if (!this.maxDriveSlots) {
          this.maxDriveSlots = parseInt(modal.dataset.maxDrives) || 0;
        }
        
        // Update button states before opening modal
        this.updateDriveButtonStates();
        
        // Calculate available slots
        const availableSlots = this.maxDriveSlots - this.usedDriveSlots;
        
        // Setup quantity controls for all items in modal
        modal.querySelectorAll('.configurator__driver-list-item').forEach(item => {
          const input = item.querySelector('.quantity__input');
          const plusButton = item.querySelector('.quantity__button[name="plus"]');
          const minusButton = item.querySelector('.quantity__button[name="minus"]');
          
          // Remove existing listeners
          const newPlusBtn = plusButton.cloneNode(true);
          const newMinusBtn = minusButton.cloneNode(true);
          plusButton.parentNode.replaceChild(newPlusBtn, plusButton);
          minusButton.parentNode.replaceChild(newMinusBtn, minusButton);
          
          // Add new listeners
          newPlusBtn.addEventListener('click', (e) => {
            e.preventDefault();
            const currentValue = parseInt(input.value) || 1;
            if (currentValue < availableSlots) {
              input.value = currentValue + 1;
              newMinusBtn.disabled = false;
              if (currentValue + 1 >= availableSlots) {
                newPlusBtn.disabled = true;
              }
            }
          });
          
          newMinusBtn.addEventListener('click', (e) => {
            e.preventDefault();
            const currentValue = parseInt(input.value) || 1;
            if (currentValue > 1) {
              input.value = currentValue - 1;
              newPlusBtn.disabled = false;
              if (currentValue - 1 <= 1) {
                newMinusBtn.disabled = true;
              }
            }
          });
          
          // Prevent direct input from exceeding limits
          input.addEventListener('change', () => {
            let value = parseInt(input.value) || 1;
            value = Math.max(1, Math.min(value, availableSlots));
            input.value = value;
            newPlusBtn.disabled = value >= availableSlots;
            newMinusBtn.disabled = value <= 1;
          });
          
          // Prevent mousewheel from changing value
          input.addEventListener('wheel', (e) => e.preventDefault());
          
          // Set initial button states
          const currentValue = parseInt(input.value) || 1;
          newPlusBtn.disabled = currentValue >= availableSlots;
          newMinusBtn.disabled = currentValue <= 1;
        });
        
        this.openModal(modal);
      }
    });
  }

  setupDriveSelections() {
    const drivesContainer = this.querySelector('#configurator-drives');
    if (!drivesContainer) return;
    
    this.maxDriveSlots = parseInt(drivesContainer.dataset.maxDrives) || 0;
    
    // Setup all drive cards (both recommended and modal)
    this.querySelectorAll('.configurator__card').forEach(card => {
      this.setupDriveCardListeners(card);
    });
  
    // Initialize selected state for recommended drives
    this.querySelectorAll('#configurator-drives .configurator__card').forEach(card => {
      const input = card.querySelector('.quantity__input');
      const driveId = input?.dataset.driveId;
      
      if (driveId && this.selectedDrives.has(driveId)) {
        this.updateCardSelectedState(card, true);
      }
    });
  
    // Initial setup
    this.updateAllDriveControls();
  }
  
  setupDriveCardListeners(card) {
    const input = card.querySelector('.quantity__input');
    const selectButton = card.querySelector('.configurator__select-btn');
    const changeButton = card.querySelector('.configurator__change-btn');
    
    if (!input || !selectButton) return;
    
    const driveId = input.dataset.driveId;
  
    // Setup quantity controls
    card.querySelectorAll('.quantity__button').forEach(button => {
      button.addEventListener('click', () => this.handleQuantityChange(button, card));
    });
  
    // Setup select button
    selectButton.addEventListener('click', () => this.handleDriveSelection(card));
  
    // Setup change button
    if (changeButton) {
      changeButton.addEventListener('click', () => {
        
      });
    }
  }

  handleQuantityChange(element, card) {
    const input = element.classList.contains('quantity__input') ? 
      element : 
      element.parentElement.querySelector('.quantity__input');
    
    const currentValue = parseInt(input.value);
    const isButton = element.classList.contains('quantity__button');
    const isPlus = isButton && element.name === 'plus';
    const newValue = isButton ? 
      (isPlus ? currentValue + 1 : currentValue - 1) : 
      parseInt(input.value);
    
    const driveId = input.dataset.driveId;
  
    // Calculate total used slots excluding this drive
    const otherSlotsUsed = Array.from(this.selectedDrives.entries())
      .filter(([id, _]) => id !== driveId)
      .reduce((sum, [_, qty]) => sum + qty, 0);
  
    // Calculate maximum allowed value
    const maxAllowed = this.maxDriveSlots - otherSlotsUsed;
    
    // Ensure value is within bounds (always between 1 and maxDriveSlots)
    const boundedValue = Math.max(1, Math.min(newValue, this.maxDriveSlots));
    
    // Update input value
    input.value = boundedValue;
  
    // Update price and selected quantity if drive is selected
    if (this.selectedDrives.has(driveId)) {
      // Only allow changes that don't exceed total slot limit
      if (otherSlotsUsed + boundedValue <= this.maxDriveSlots) {
        this.selectedDrives.set(driveId, boundedValue);
        this.usedDriveSlots = otherSlotsUsed + boundedValue;
        this.updateAllDriveControls();
        this.dispatchEvent(new CustomEvent('configuration-change'));
        this.updateDriverCardPrice(card, boundedValue);
      } else {
        // Reset to previous valid value
        const previousValue = this.selectedDrives.get(driveId);
        input.value = previousValue;
      }
    } else {
      this.updateDriverCardPrice(card, boundedValue);
    }
  
    this.updateDriveQuantityControls(input);
  }

  handleDriveSelection(card) {
    const input = card.querySelector('.quantity__input');
    const driveId = input.dataset.driveId;
    const quantity = parseInt(input.value);
  
    if (this.selectedDrives.has(driveId)) {
      // Remove drive
      this.selectedDrives.delete(driveId);
      this.usedDriveSlots -= quantity;
      this.updateCardSelectedState(card, false);
      this.updateDriverCardPrice(card, 1);
      input.value = 1;
    } else if ((this.usedDriveSlots + quantity) <= this.maxDriveSlots) {
      // Add drive
      this.selectedDrives.set(driveId, quantity);
      this.usedDriveSlots += quantity;
      this.updateCardSelectedState(card, true);
      this.updateDriverCardPrice(card, quantity);
    }
  
    this.updateAllDriveControls();
    this.updateDriveButtonStates();
    this.dispatchEvent(new CustomEvent('configuration-change'));
  }

  updateCardSelectedState(card, isSelected) {
    const selectButton = card.querySelector('.configurator__select-btn');
    const changeButton = card.querySelector('.configurator__change-btn');
  
    selectButton.textContent = isSelected ? 'Remove' : '+ Add';
    if (changeButton) {
      // Hide change button if drive is selected
      changeButton.style.display = isSelected ? 'none' : 'none';
    }
    card.classList.toggle('configurator__card--selected', isSelected);
    // this.enableDriveQuantityControls(card, !isSelected);
  }
  
  updateDriverCardPrice(card, quantity) {
    const priceElement = card.querySelector('.configurator__card-price');
    const basePrice = parseFloat(priceElement.dataset.price) || 0;
    
    if (priceElement && !priceElement.dataset.included) {
      priceElement.textContent = `+${this.formatMoney(basePrice * quantity)}`;
    }
  }
  
  openDriveModal() {
    const modal = document.querySelector('.configurator__driver-modal');
    if (modal) {
      modal.querySelector('.configurator__modal-close').click();
      this.querySelector('.configurator__driver-modal-show-btn').click();
    }
  }
  
  updateDriveQuantityControls(input) {
    const currentValue = parseInt(input.value);
    const card = input.closest('.configurator__card');
    const plusBtn = card.querySelector('.quantity__button[name="plus"]');
    const minusBtn = card.querySelector('.quantity__button[name="minus"]');
    const driveId = input.dataset.driveId;
  
    // Calculate remaining slots
    const otherSlotsUsed = Array.from(this.selectedDrives.entries())
      .filter(([id, _]) => id !== driveId)
      .reduce((sum, [_, qty]) => sum + qty, 0);
  
    const isSelected = this.selectedDrives.has(driveId);
    const currentDriveQuantity = isSelected ? this.selectedDrives.get(driveId) : 0;
    const availableSlots = this.maxDriveSlots - otherSlotsUsed;
  
    const maxAllowed = isSelected ? 
      Math.min(this.maxDriveSlots - otherSlotsUsed + currentDriveQuantity, this.maxDriveSlots) : 
      availableSlots;
  
    plusBtn.disabled = currentValue >= maxAllowed;
    minusBtn.disabled = currentValue <= 1;
  }

  enableDriveQuantityControls(card, enabled) {
    const quantityWrapper = card.querySelector('.quantity');
    if (enabled) {
      quantityWrapper.classList.remove('quantity--disabled');
    } else {
      quantityWrapper.classList.add('quantity--disabled');
    }
  }
  
  updateAllDriveControls() {
    this.querySelectorAll('#configurator-drives .configurator__card').forEach(card => {
      const quantityInput = card.querySelector('.quantity__input');
      const driveId = quantityInput.dataset.driveId;
      
      // Update quantity controls
      this.updateDriveQuantityControls(quantityInput);
      
      // Update disabled state based on selection
      this.enableDriveQuantityControls(card, !this.selectedDrives.has(driveId));

      // Update select button text
      const selectBtn = card.querySelector('.configurator__select-btn');
      selectBtn.textContent = this.selectedDrives.has(driveId) ? 'X Remove' : '+ Add';

      // Disable select button if we can't add this drive
      const quantity = parseInt(quantityInput.value);
      const canAdd = (this.usedDriveSlots + quantity) <= this.maxDriveSlots;
      selectBtn.disabled = !this.selectedDrives.has(driveId) && !canAdd;
      
      if (selectBtn.disabled) {
        selectBtn.classList.add('configurator__select-btn--disabled');
      } else {
        selectBtn.classList.remove('configurator__select-btn--disabled');
      }
    });

    // Update slots counter if it exists
    const slotsCounter = this.querySelector('.configurator__slots-used');
    if (slotsCounter) {
      slotsCounter.textContent = this.usedDriveSlots;
    }
  }

  setupDriverCapacityFilter() {
    const container = document.getElementById('capacity-filter-container');
    if (!container) return;

    // Collect all capacity values
    const capacityItems = document.querySelectorAll('.configurator__driver-list-item');
    const capacities = new Set();
    capacityItems.forEach(item => {
      const capacity = item.dataset.capacity;
      if (capacity) capacities.add(capacity);
    });


    // Sort capacities
    const sortedCapacities = Array.from(capacities).sort((a, b) => {
      return this.convertToGB(a) - this.convertToGB(b);
    });

    // Create select element
    const select = document.createElement('select');
    select.className = 'configurator__filter-select';
    select.dataset.filterType = 'capacity';

    // Add default option
    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = 'All Capacities';
    select.appendChild(defaultOption);

    // Add sorted capacity options
    sortedCapacities.forEach(capacity => {
      const option = document.createElement('option');
      option.value = capacity;
      option.textContent = capacity;
      select.appendChild(option);
    });

    // Add change event listener
    select.addEventListener('change', () => {
      this.filterDrivers();
    });

    // Add to container
    container.appendChild(select);
  }

  setupDriverFilters() {
    // Setup capacity filter
    this.setupDriverCapacityFilter();
    
    // Setup sort and other filters
    const filterSelects = document.querySelectorAll('.configurator__filter-select');
    filterSelects.forEach(select => {
      select.addEventListener('change', () => {
        if (select.dataset.filterType === 'sort') {
          this.sortDrivers(select.value);
        }
        this.filterDrivers();
      });
    });

    // Add handler for modal drive selection buttons
    document.querySelectorAll('.configurator__driver-list-item .configurator__add-btn').forEach(button => {
      button.addEventListener('click', (e) => {
        const listItem = e.target.closest('.configurator__driver-list-item');
        
        // Get all quantity inputs in the list item
        const allInputs = listItem.querySelectorAll('.quantity__input');
        let quantity = 1;

        // Try to get quantity from visible input
        allInputs.forEach(input => {
          const wrapper = input.closest('.configurator__quantity-wrapper');
          if (wrapper && window.getComputedStyle(wrapper).display !== 'none') {
            quantity = parseInt(input.value) || 1;
          }
        });
        // Get drive information from the list item
        const driveInfo = {
          id: listItem.dataset.id,
          title: listItem.querySelector('.configurator__item-title').textContent,
          condition: listItem.dataset.condition,
          capacity: listItem.dataset.capacity,
          type: listItem.dataset.type,
          price: listItem.dataset.price,
          variantId: listItem.dataset.variantId, 
          consumption: listItem.dataset.consumption,
          quantity: quantity
        };

        // Create and add the card to the drives section
        this.createDriveCard(driveInfo);
        
        // Remove the item from the modal
        listItem.remove();
        
        // Close the modal
        const modal = document.querySelector('.configurator__driver-modal');
        this.closeModal(modal);
      });
    });

    // Add quantity controls for modal
    document.querySelectorAll('.configurator__driver-modal .quantity__button').forEach(button => {
      button.addEventListener('click', (e) => {
        const input = button.parentElement.querySelector('.quantity__input');
        const currentValue = parseInt(input.value);
        const isPlus = button.name === 'plus';
        const newValue = isPlus ? currentValue + 1 : currentValue - 1;
        const availableSlots = this.maxDriveSlots - this.usedDriveSlots;
    
        if (newValue >= 1 && newValue <= availableSlots) {
          input.value = newValue;
          const plusBtn = button.parentElement.querySelector('[name="plus"]');
          const minusBtn = button.parentElement.querySelector('[name="minus"]');
          if (plusBtn) plusBtn.disabled = newValue >= availableSlots;
          if (minusBtn) minusBtn.disabled = newValue <= 1;
        }
      });
    });

    // Update direct input handling for quantity
    document.querySelectorAll('.configurator__driver-modal .quantity__input').forEach(input => {
      input.addEventListener('change', (e) => {
        const newValue = parseInt(input.value);
        const availableSlots = this.maxDriveSlots - this.usedDriveSlots;
        
        let boundedValue;
        if (newValue < 1) {
          boundedValue = 1;
        } else if (newValue > availableSlots) {
          boundedValue = availableSlots;
        } else {
          boundedValue = newValue;
        }
        
        input.value = boundedValue;
        
        // Update button states
        const plusBtn = input.parentElement.querySelector('[name="plus"]');
        const minusBtn = input.parentElement.querySelector('[name="minus"]');
        if (plusBtn) plusBtn.disabled = boundedValue >= availableSlots;
        if (minusBtn) minusBtn.disabled = boundedValue <= 1;
      });
    });
  }

  filterDrivers() {
    const conditionFilter = document.querySelector('.configurator__filter-select[data-filter-type="condition"]').value;
    const capacityFilter = document.querySelector('.configurator__filter-select[data-filter-type="capacity"]').value;
    const typeFilter = document.querySelector('.configurator__filter-select[data-filter-type="type"]').value;
    
    const items = document.querySelectorAll('.configurator__driver-list-item');
    
    items.forEach(item => {
      const condition = item.dataset.condition;
      const capacity = item.dataset.capacity;
      const type = item.dataset.type;
      
      const matchesCondition = !conditionFilter || condition === conditionFilter;
      const matchesCapacity = !capacityFilter || capacity === capacityFilter;
      const matchesType = !typeFilter || type === typeFilter;
      
      item.style.display = matchesCondition && matchesCapacity && matchesType ? '' : 'none';
    });
  }

  sortDrivers(sortValue) {
    this.sortItems(
      sortValue, 
      '.configurator__driver-list', 
      '.configurator__item-price', 
      false
    );
  }

  createDriveCard(driveInfo) {
    const drivesContainer = this.querySelector('#configurator-drives');
    if (!drivesContainer) return;

    // Check if card already exists
    const existingCard = this.querySelector(`[data-drive-id="${driveInfo.id}"]`)?.closest('.configurator__card');
    if (existingCard) {
      // If exists, update quantity and select it
      const quantityInput = existingCard.querySelector('.quantity__input');
      if (quantityInput) {
        quantityInput.value = driveInfo.quantity;
        this.handleQuantityChange(quantityInput, existingCard);
        if (!this.selectedDrives.has(driveInfo.id)) {
          const selectButton = existingCard.querySelector('.configurator__select-btn');
          setTimeout(() => selectButton.click(), 0);
        }
      }
      return;
    }

    const card = document.createElement('li');
    card.className = 'configurator__card';
    card.innerHTML = `
      <div class="configurator__card-content">
        <div class="configurator__card__header-wrapper">
          <div class="configurator__card-header">
            <h3 class="configurator__card-title">${driveInfo.title}</h3>
            <p class="configurator__card-price" 
              data-price="${driveInfo.price}"
              data-consumption="${driveInfo.consumption || 0}">
              +${this.formatMoney(driveInfo.price)}
            </p>
          </div>
          <span class="configurator__card-meta">
            ${driveInfo.condition} ${driveInfo.type ? `${driveInfo.type}` : ''}
          </span> 
        </div>
        <div class="configurator__card-actions">
          <div class="quantity">
            <button class="quantity__button" name="minus" type="button">
              <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-minus" fill="none" viewBox="0 0 10 2">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M1 1C1 0.7 1.3 0.7 1 1H9C9.3 1 9.3 1.3 9 1C9 1.3 8.7 1.3 9 1H1C0.7 1 0.7 0.7 1 1Z" fill="currentColor"/>
              </svg>
            </button>
            <input class="quantity__input"
                  type="number"
                  name="drive-quantity[${driveInfo.id}]"
                  min="1"
                  max="${this.maxDriveSlots}"
                  value="${driveInfo.quantity}"
                  data-drive-id="${driveInfo.id}">
            <button class="quantity__button" name="plus" type="button">
              <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-plus" fill="none" viewBox="0 0 10 10">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M1 4.51C1 4.23 1.23 4 1.5 4H4.5V1.5C4.5 1.23 4.73 1 5 1C5.27 1 5.5 1.23 5.5 1.5V4H8.5C8.77 4 9 4.23 9 4.5C9 4.77 8.77 5 8.5 5H5.5V8.5C5.5 8.77 5.27 9 5 9C4.73 9 4.5 8.77 4.5 8.5V5H1.5C1.23 5 1 4.77 1 4.51Z" fill="currentColor"/>
              </svg>
            </button> 
          </div>
          <button class="configurator__select-btn" 
            type="button" 
            data-drive-id="${driveInfo.id}"
            data-variant-id="${driveInfo.variantId}">
            + Add
          </button>
        </div>
      </div>
    `;

    // Add the card to the container
    drivesContainer.appendChild(card);

    // Setup other card listeners
    this.setupDriveCardListeners(card);

    // Set quantity before selecting
    const quantityInput = card.querySelector('.quantity__input');
    if (quantityInput) {
      quantityInput.value = driveInfo.quantity;
      // Important: Update quantity state before selection
      this.handleQuantityChange(quantityInput, card);
    }

    // Select the drive after quantity is set
    const selectButton = card.querySelector('.configurator__select-btn');
    setTimeout(() => selectButton.click(), 0);
  }

  updateDriveButtonStates() {
    const availableSlots = this.maxDriveSlots - this.usedDriveSlots;
  
    // Update modal buttons
    const modal = document.querySelector('.configurator__driver-modal');
    if (modal) {
      modal.querySelectorAll('.configurator__add-btn').forEach(button => {
        const shouldDisable = availableSlots === 0;
        button.disabled = shouldDisable;
        button.classList.toggle('configurator__add-btn--disabled', shouldDisable);
      });
    }
  
    // Update only "Add" buttons in the main configurator
    document.querySelectorAll('.configurator__select-btn').forEach(button => {
      const isRemoveButton = button.textContent.trim() === 'X Remove';
      
      if (isRemoveButton) {
        button.disabled = false;
        button.classList.remove('configurator__select-btn--disabled');
      } else {
        const shouldDisable = availableSlots === 0;
        button.disabled = shouldDisable;
        button.classList.toggle('configurator__select-btn--disabled', shouldDisable);
      }
    });
  }

  // ************* InitiaFilter Related Methods *************
  setupFilters() {
    this.querySelectorAll('.configurator__filter-btn').forEach(button => {
      button.addEventListener('click', () => {
        const filterGroup = button.closest('.configurator__filter-group');
        
        // Remove active class from all buttons in this group
        filterGroup.querySelectorAll('.configurator__filter-btn').forEach(btn => {
            btn.classList.remove('configurator__filter-btn--active');
        });
        
        // Add active class to clicked button
        button.classList.add('configurator__filter-btn--active');
        
        // Get the section and update cards
        const section = button.closest('.configurator__section');
        this.updateVisibleCards(section);

        // Handle OS select visibility if this is an OS filter
        if (button.dataset.filterField === 'filter_operation_system') {
          const osType = button.dataset.filterValue;
          this.updateOsSelectVisibility(osType);
        }
      });
    });
  }

  setupInitialFilters() {
    this.querySelectorAll('.configurator__section').forEach(section => {
      const defaultFilters = {};
      
      // Get all default (first) filter values
      section.querySelectorAll('.configurator__filter-btn[data-is-default="true"]').forEach(button => {
        const field = button.dataset.filterField;
        const value = button.dataset.filterValue;
        if (field && value) {
          defaultFilters[field] = value;
        }
      });
      
      // Apply initial visibility
      if (Object.keys(defaultFilters).length > 0) {
        this.updateVisibleCards(section);
      }

      // Calculate initial price differences
      this.updatePriceDifferences(section);
    });
  }

  // ************* Helper Methods *************

  convertToGB(sizeString) {
    if (!sizeString) return 0;
    const number = parseFloat(sizeString.trim());
    if (isNaN(number)) return 0;
    
    if (sizeString.includes('TB')) return number * 1024;
    if (sizeString.includes('GB')) return number;
    return number; // Fallback
  }

  sortItems(sortValue, listSelector, priceSelector, isPriceAttribute = true) {
    const list = document.querySelector(listSelector);
    if (!list) return;
    
    const items = Array.from(list.children);
  
    items.sort((a, b) => {
      let priceA, priceB;
      
      if (isPriceAttribute) {
        // Get price from data attribute
        priceA = parseFloat(a.querySelector(priceSelector).dataset.price);
        priceB = parseFloat(b.querySelector(priceSelector).dataset.price);
      } else {
        // Get price from text content
        priceA = parseFloat(a.querySelector(priceSelector).textContent.replace(/[^0-9.-]+/g, ''));
        priceB = parseFloat(b.querySelector(priceSelector).textContent.replace(/[^0-9.-]+/g, ''));
      }
  
      if (sortValue === 'price-asc') {
        return priceA - priceB;
      } else if (sortValue === 'price-desc') {
        return priceB - priceA;
      }
      return 0;
    });
  
    // Clear and re-append sorted items
    items.forEach(item => list.appendChild(item));
  }

  updateVisibleCards(section) {
    this.updateProcessorVisibleCards(section);
    this.updateMemoryVisibleCards(section);
  }

  updateMemoryVisibleCards(section) {
    const memoryCards = section.querySelectorAll('#configurator-memory .configurator__card');
    // Update memory cards
    if (memoryCards.length > 0) {
      this.updateMemoryCards();
    }
  }

  updateDropdownPriceDifferences(select) {
    const selectedOption = select.options[select.selectedIndex];
    const selectedPrice = parseFloat(selectedOption.dataset.price) || 0;
    const isOsSelect = select.name === 'os-selection';
  
    if (isOsSelect) {
      // Update prices in all OS dropdowns
      this.querySelectorAll('.configurator__dropdown[name="os-selection"]').forEach(dropdown => {
        Array.from(dropdown.options).forEach(option => {
          if (option.value === '') return; // Skip placeholder options
          
          const optionPrice = parseFloat(option.dataset.price) || 0;
          const originalText = option.textContent.split('(')[0].trim();
          const priceDifference = optionPrice - selectedPrice;

          if (DISPLAY_LINE_ITEM_PRICE) {
            if (priceDifference === 0) {
              option.textContent = originalText;
            } else {
              const formattedDifference = this.formatMoney(priceDifference);
              option.textContent = `${originalText} (${
                priceDifference > 0 ? '+' : ''
              }${formattedDifference})`;
            }
          } else {
            option.textContent = originalText;
          }
        });
      });
    } else {
      // Original behavior for non-OS dropdowns
      Array.from(select.options).forEach(option => {
        if (option.value === '') return;
        
        const optionPrice = parseFloat(option.dataset.price) || 0;
        const originalText = option.textContent.split('(')[0].trim();
        const priceDifference = optionPrice - selectedPrice;

        if (DISPLAY_LINE_ITEM_PRICE) {
          if (option === selectedOption) {
            option.textContent = originalText;
          } else {
            const formattedDifference = this.formatMoney(priceDifference);
            option.textContent = `${originalText} (${
              priceDifference > 0 ? '+' : ''
            }${formattedDifference})`;
          }
        } else {
          option.textContent = originalText;
        }
      });
    }
  }
  
  // ************* OS Related Methods *************
  updateOsSelectVisibility(selectedType) {
    // Convert to handleized format to match the data attribute
    const handleizedType = selectedType.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    
    const dropdownWrappers = this.querySelectorAll('.configurator__dropdown-wrapper[data-os-type]');
    dropdownWrappers.forEach(wrapper => {
      const isVisible = wrapper.dataset.osType === handleizedType;
      wrapper.style.display = isVisible ? '' : 'none';
    });
  }

  // Add this new method to handle OS dropdown initialization
  setupOsDropdowns() {
    // Initialize price differences for default selections
    this.querySelectorAll('.configurator__dropdown[name="os-selection"]').forEach(select => {
      if (select.value) {
        this.updateDropdownPriceDifferences(select);
      }
    });

    // Add change event listeners to dropdowns
    this.querySelectorAll('.configurator__dropdown[name="os-selection"]').forEach(select => {
      select.addEventListener('change', () => {
        // Reset other dropdowns
        this.querySelectorAll('.configurator__dropdown[name="os-selection"]').forEach(dropdown => {
          if (dropdown !== select) dropdown.selectedIndex = 0;
        });

      this.updateDropdownPriceDifferences(select);
      this.dispatchEvent(new CustomEvent('configuration-change'));

      // Handle error message
      const errorMessage = select.closest('.configurator__section').querySelector('.configurator__error-message');
      if (errorMessage) {
        errorMessage.style.display = select.value ? 'none' : 'block';
        }
      });
    });
  }

  setupNetworkCardDropdown() {
    const networkSelect = this.querySelector('.configurator__dropdown[name="network-card-selection"]');
    if (!networkSelect) return;
  
    networkSelect.addEventListener('change', () => {
      this.updateDropdownPriceDifferences(networkSelect);
      this.dispatchEvent(new CustomEvent('configuration-change'));
    });
  }

  cardMatchesFilters(card, activeFilters) {
    return Object.entries(activeFilters).every(([field, value]) => {
      const cardValue = card.dataset[field];
      return cardValue === value;
    });
  }

  compareValues(a, b) {
    // Extract numbers for numerical comparison
    const numA = parseFloat(a.replace(/[^\d.]/g, ''));
    const numB = parseFloat(b.replace(/[^\d.]/g, ''));
    
    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB;
    }
    return a.localeCompare(b);
  }

  updateModalButtonCount(section) {
    const modalButton = section.querySelector('.configurator__modal-show-btn');
    if (!modalButton) return;

    const modalType = modalButton.dataset.modalType;
    const modal = document.querySelector(`.configurator__modal[data-modal-type="${modalType}"]`);
    
    if (!modal) return;

    const activeFilters = this.getProcessorActiveFilters(section);
    const modalItems = modal.querySelectorAll('.configurator__list-item');
    
    const visibleCount = Array.from(modalItems).filter(item => {
      return Object.entries(activeFilters).every(([field, value]) => {
        const itemValue = item.dataset[field];
        return itemValue === value;
      });
    }).length;

    modalButton.textContent = `Select From ${visibleCount} Options`;
  }

  // Card Selection Related Methods
  setupCardSelectors() {
    const raidSection = this.querySelector('#raid-controllers-section');
    if (raidSection) {
      const defaultRaidInput = raidSection.querySelector('input[type="radio"]:checked');
      if (defaultRaidInput) {
        const card = defaultRaidInput.closest('.configurator__card');
        card.classList.add('configurator__card--selected');
        this.updatePriceDifferences(raidSection);
        }
      }

    this.querySelectorAll('.configurator__selector').forEach(selector => {
      selector.addEventListener('change', (event) => {
        if (event.target.checked) {
          const section = event.target.closest('.configurator__section');
          const card = event.target.closest('.configurator__card');
          
          // Special handling for RAID controllers
          if (section && section.id === 'raid-controllers-section') {
            // Clear selected state from all cards in all subsections
            section.querySelectorAll('.configurator__card').forEach(otherCard => {
              otherCard.classList.remove('configurator__card--selected');
            });
          } else {
            // Regular handling for other sections
            const ul = card.closest('ul');
            ul.querySelectorAll('.configurator__card').forEach(otherCard => {
              otherCard.classList.remove('configurator__card--selected');
            });
          }
          
          card.classList.add('configurator__card--selected');
          this.updatePriceDifferences(section);
          this.dispatchEvent(new CustomEvent('configuration-change'));
        }
      });
    });
  }

  getSelectedDrivePrices() {
    let total = 0;
    this.selectedDrives.forEach((quantity, driveId) => {
      const input = this.querySelector(`input[data-drive-id="${driveId}"]`);
      const card = input?.closest('.configurator__card');
      const priceElement = card?.querySelector('.configurator__card-price');
      if (priceElement) {
        const price = parseFloat(priceElement.dataset.price || '0');
        total += price * quantity; // Multiply price by quantity
      }
    });
    return total;
  }
  
  getSelectedDriveConsumption() {
    let total = 0;
    this.selectedDrives.forEach((quantity, driveId) => {
      const driveCard = this.querySelector(`.configurator__card input[data-drive-id="${driveId}"]`)?.closest('.configurator__card');
      const priceElement = driveCard?.querySelector('.configurator__card-price');
      if (priceElement) {
        const consumption = parseFloat(priceElement.dataset.consumption) || 0;
        total += consumption * quantity;
      }
    });
  return total;
  }

  setupMemorySizeFilter() {
    const filterContainer = this.querySelector('.configurator__memory-size-filter');
    if (!filterContainer) return;

    // Get memory sizes and default value from data attributes
    const memorySizes = filterContainer.dataset.memorySizes.split(',');
    const defaultSize = filterContainer.dataset.defaultSize;
    
   // Sort memory sizes
    const sortedSizes = memorySizes
      .filter(size => size.trim())
      .sort((a, b) => {
    return this.convertToGB(a) - this.convertToGB(b);
    });

    // Create the select wrapper and elements
    const wrapper = document.createElement('div');
    wrapper.className = 'configurator__dropdown-wrapper';


    const select = document.createElement('select');
    select.className = 'configurator__dropdown';
    select.id = 'memory-size-filter';
    select.dataset.filterType = 'memory_size';

    // Add the "All Sizes" option
    const defaultOption = document.createElement('option');
    defaultOption.value = 'all';
    defaultOption.textContent = 'Select Total Memory';
    if (!defaultSize) {
      defaultOption.selected = true;
    }
    select.appendChild(defaultOption);

    // Add sorted size options
    sortedSizes.forEach(size => {
      const option = document.createElement('option');
      const trimmedSize = size.trim();
      option.value = trimmedSize;
      option.textContent = trimmedSize;
      
      // Set as selected if it matches the default value
      if (defaultSize && trimmedSize === defaultSize) {
        option.selected = true;
      }
      
      select.appendChild(option);
    });

    // Update the change event listener with immediate height adjustment
    select.addEventListener('change', () => {
      const section = select.closest('.configurator__section');
      const contentWrapper = section.querySelector('.configurator__content-wrapper');
      
      // Update cards visibility
      this.updateMemoryCards();
      
      // Set height to auto immediately
      if (section.open && contentWrapper) {
        contentWrapper.style.height = 'auto';
      }
    });

    wrapper.appendChild(select);
    filterContainer.appendChild(wrapper);

    // Trigger initial filter update
    this.updateMemoryCards();
  }

  updateMemoryCards() {
    const memoryCards = this.querySelectorAll('#configurator-memory .configurator__card');
    const speedFilter = this.querySelector('[data-filter-field="filter_memory_speed"].configurator__filter-btn--active');
    const sizeFilter = this.querySelector('#memory-size-filter');

    if (!memoryCards.length) return;

    memoryCards.forEach(card => {  
      const matchesSpeed = !speedFilter || card.dataset.filter_memory_speed === speedFilter.dataset.filterValue;
      const matchesSize = !sizeFilter || sizeFilter.value === 'all' || 
                         (card.dataset.memory_size && card.dataset.memory_size === sizeFilter.value);
      
      card.style.display = matchesSpeed && matchesSize ? '' : 'none';
    });
  }

  setupPreBuiltCustomization() {
    this.querySelectorAll('.configurator__pre-built-customize-btn').forEach(button => {
      button.addEventListener('click', (e) => {
        const preBuiltCard = e.target.closest('.configurator__pre-built-card');
        
        // Switch to customize tab
        this.switchToCustomizeTab();
  
        // Apply pre-built selections
        this.applyPreBuiltSelections(preBuiltCard);
      });
    });
  }

  setupPreBuiltSelection() {
    this.querySelectorAll('.configurator__pre-built-select-btn').forEach(button => {
      button.addEventListener('click', async (e) => {
        const preBuiltCard = e.target.closest('.configurator__pre-built-card');
        
        // Initialize loading state
        const loadingButton = this.createLoadingButton(button);
        loadingButton.startLoading();
        
        try {
          // Apply pre-built selections (silently)
          await this.applyPreBuiltSelections(preBuiltCard, true);
          
        } catch (error) {
          console.error('Error selecting pre-built system:', error);
          alert(`Failed to add pre-built system: ${error.message}`);
        } finally {
          loadingButton.stopLoading();
          setTimeout(async () => {
            await this.addToCart();
          }, 100);
        }
      });
    });
  }

  switchToCustomizeTab() {
    const customizeTab = this.querySelector('[data-tab="customize"]');
    customizeTab.click();
  }

  calculatePreBuiltPrice(preBuiltCard) {
    if (!preBuiltCard) return 0;
  
    // Get base price from the configurator
    let totalPrice = parseFloat(preBuiltCard.dataset.basePrice) || 0;  
    // Get all specs with price data
    const specElements = preBuiltCard.querySelectorAll('.configurator__pre-built-card-spec[data-pre-build-item-price]');
    
    // Sum up all component prices
    specElements.forEach(spec => {
      const itemPrice = parseFloat(spec.dataset.preBuildItemPrice) || 0;
      totalPrice += itemPrice;
    });
  
    // Update the price display in the pre-built card
    const priceDisplay = preBuiltCard.querySelector('.configurator__pre-built-card-price');
    if (priceDisplay) {
      priceDisplay.textContent = this.formatMoney(totalPrice);
    }
  
    return totalPrice;
  }

  setupPreBuiltPrices() {
    const preBuiltCards = this.querySelectorAll('.configurator__pre-built-card');
    preBuiltCards.forEach(card => {
      this.calculatePreBuiltPrice(card);
    });
  }

  async applyPreBuiltSelections(preBuiltCard, silent = false) {
    if (!preBuiltCard) return;
  
    // Get all component IDs (referencing lines 1390-1399)
    const componentIds = {
      processor: preBuiltCard.querySelector('[data-processor-id]')?.dataset.processorId,
      heatsink: preBuiltCard.querySelector('[data-heatsink-id]')?.dataset.heatsinkId,
      memory: preBuiltCard.querySelector('[data-memory-id]')?.dataset.memoryId,
      raid: preBuiltCard.querySelector('[data-raid-id]')?.dataset.raidId,
      remote: preBuiltCard.querySelector('[data-remote-id]')?.dataset.remoteId,
      power: preBuiltCard.querySelector('[data-power-id]')?.dataset.powerId,
      network: preBuiltCard.querySelector('[data-network-id]')?.dataset.networkId,
      os: preBuiltCard.querySelector('[data-os-id]')?.dataset.osId
    };
  
    // Get all driver specs
    const driverSpecs = Array.from(preBuiltCard.querySelectorAll('[data-drivers-id]')).map(spec => ({
      id: spec.dataset.driversId,
      quantity: parseInt(spec.dataset.quantity) || 1
    }));
    
    // Apply selections for each component
    const promises = [];
    if (componentIds.processor) promises.push(this.selectPreBuiltProcessor(componentIds.processor, silent));
    if (componentIds.heatsink) promises.push(this.selectPreBuiltHeatsink(componentIds.heatsink, silent));
    if (componentIds.memory) promises.push(this.selectPreBuiltMemory(componentIds.memory, silent));
    if (componentIds.raid) promises.push(this.selectPreBuiltRaid(componentIds.raid, silent));
    if (componentIds.remote) promises.push(this.selectPreBuiltComponent(componentIds.remote, 'configurator-remotes', silent));
    if (componentIds.power) promises.push(this.selectPreBuiltComponent(componentIds.power, 'configurator-power-supplies', silent));
    if (componentIds.network) promises.push(this.selectPreBuiltComponent(componentIds.network, 'configurator-network-cards', silent));
    if (componentIds.os) promises.push(this.selectPreBuiltOS(componentIds.os, silent));
  
    // Apply driver selections
    if (driverSpecs.length > 0) {
      promises.push(this.selectPreBuiltDrivers(driverSpecs, silent));
    }
  
    // Wait for all selections to complete
    await Promise.all(promises);
  
    // Only switch tab if not in silent mode
    if (!silent) {
      this.switchToCustomizeTab();
    }
  }

  selectPreBuiltDrivers(driverSpecs) {
  // Clear any existing selected drives first
  this.selectedDrives.clear();
  this.usedDriveSlots = 0; // Reset used slots
  
  driverSpecs.forEach(spec => {
    const driveId = spec.id.toString();
    const modalDrive = document.querySelector(`.configurator__driver-modal .configurator__driver-list-item[data-id="${driveId}"]`);
    
    if (!modalDrive) {
      console.warn(`Modal drive not found for ID: ${driveId}`);
      return;
    }

    // Check if card already exists
    const existingCard = this.querySelector(`[data-drive-id="${driveId}"]`)?.closest('.configurator__card');
    
    if (existingCard) {
      // Handle existing card
      const quantityInput = existingCard.querySelector('.quantity__input');
      if (quantityInput) {
        quantityInput.value = spec.quantity;
        this.handleQuantityChange(quantityInput, existingCard);
      }
      this.handleDriveSelection(existingCard);
    } else {
      // Create new card from modal data
      const driveData = {
        id: driveId,
        quantity: spec.quantity,
        title: modalDrive.querySelector('.configurator__item-title').textContent,
        price: modalDrive.dataset.price,
        condition: modalDrive.dataset.condition,
        capacity: modalDrive.dataset.capacity,
        type: modalDrive.dataset.type,
        variantId: modalDrive.dataset.variantId
      };

      this.createDriveCard(driveData);
      }
    });

    // Update UI elements
    setTimeout(() => {
      this.updateAllDriveControls();
      this.updateDriveButtonStates();
      this.dispatchEvent(new CustomEvent('configuration-change'));
    }, 100);
  }
  
  getProcessorData(processorId) {
    const modalItem = document.querySelector(`.configurator__processor-modal .configurator__processor-list-item[data-product-id="${processorId}"]`);
    if (!modalItem) return null;
  
    return {
      series: modalItem.dataset.filter_processor_serie,
      category: modalItem.dataset.filter_processor_category
    };
  }
  
  selectPreBuiltProcessor(processorId) {
    const processorsList = this.querySelector('#configurator-processors');
    if (!processorsList) return;
  
    // Get processor data from modal
    const processorData = this.getProcessorData(processorId);
    if (!processorData) return;
  
    // First apply filters to show the correct category/series
    this.applyProcessorFilters(processorData);
  
    // Then try to find existing card or create new one
    const existingCard = processorsList.querySelector(`input[value="${processorId}"]`)?.closest('.configurator__card');
    
    if (existingCard) {
      // Select existing card
      processorsList.querySelectorAll('.configurator__card').forEach(card => {
        card.classList.remove('configurator__card--selected');
        const input = card.querySelector('input[type="radio"]');
        if (input) input.checked = false;
      });
  
      existingCard.classList.add('configurator__card--selected');
      const input = existingCard.querySelector('input[type="radio"]');
      if (input) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else {
      // Get data from modal and create new card
      const modalItem = document.querySelector(`.configurator__processor-modal .configurator__processor-list-item[data-product-id="${processorId}"]`);
      if (!modalItem) return;
  
      const newCard = this.createProcessorCard({
        id: processorId,
        variantId: modalItem.dataset.variantId,
        title: modalItem.querySelector('.configurator__item-title').textContent,
        price: modalItem.querySelector('.configurator__item-price').dataset.price,
        series: processorData.series,
        category: processorData.category
      });
  
      // Clear existing selections
      processorsList.querySelectorAll('.configurator__card').forEach(card => {
        card.classList.remove('configurator__card--selected');
        const input = card.querySelector('input[type="radio"]');
        if (input) input.checked = false;
      });
  
      // Add and select new card
      processorsList.appendChild(newCard);
      const input = newCard.querySelector('input[type="radio"]');
      if (input) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      newCard.classList.add('configurator__card--selected');
    }
  
    // Update prices and trigger change event
    const section = processorsList.closest('.configurator__section');
    this.updatePriceDifferences(section);
    this.dispatchEvent(new CustomEvent('configuration-change'));
  }
  
  applyProcessorFilters(processorData) {
    const section = this.querySelector('#configurator-processors').closest('.configurator__section');
    
    // Reset filters
    section.querySelectorAll('.configurator__filter-btn').forEach(btn => {
      btn.classList.remove('configurator__filter-btn--active');
    });
  
    // Apply series filter
    const seriesButton = section.querySelector(
      `.configurator__filter-btn[data-filter-field="filter_processor_serie"][data-filter-value="${processorData.series}"]`
    );
    if (seriesButton) seriesButton.classList.add('configurator__filter-btn--active');
  
    // Apply category filter
    const categoryButton = section.querySelector(
      `.configurator__filter-btn[data-filter-field="filter_processor_category"][data-filter-value="${processorData.category}"]`
    );
    if (categoryButton) categoryButton.classList.add('configurator__filter-btn--active');
  
    this.updateVisibleCards(section);
  }

  selectPreBuiltHeatsink(heatsinkId) {
    const heatsinksList = this.querySelector('#configurator-heatsinks');
    if (!heatsinksList) return;
  
    // Find the target heatsink input
    const targetInput = heatsinksList.querySelector(`input[value="${heatsinkId}"]`);
    if (!targetInput) return;
  
    // Clear existing selections
    heatsinksList.querySelectorAll('.configurator__card').forEach(card => {
      card.classList.remove('configurator__card--selected');
      const input = card.querySelector('input[type="radio"]');
      if (input) input.checked = false;
    });
  
    // Select new heatsink
    const targetCard = targetInput.closest('.configurator__card');
    targetCard.classList.add('configurator__card--selected');
    targetInput.checked = true;
    targetInput.dispatchEvent(new Event('change', { bubbles: true }));
  
    // Update prices
    const section = heatsinksList.closest('.configurator__section');
    this.updatePriceDifferences(section);
  }

  selectPreBuiltMemory(memoryId) {
    const memoryList = this.querySelector('#configurator-memory');
    if (!memoryList) return;
  
    const memoryData = this.getMemoryData(memoryId);
    if (!memoryData) return;
  
    // Apply memory filters
    this.applyMemoryFilters(memoryData);
    
    // Find and select the memory card
    const existingCard = memoryList.querySelector(`input[value="${memoryId}"]`)?.closest('.configurator__card');
    if (!existingCard) return;
  
    // Clear existing selections
    memoryList.querySelectorAll('.configurator__card').forEach(card => {
      card.classList.remove('configurator__card--selected');
      const input = card.querySelector('input[type="radio"]');
      if (input) input.checked = false;
    });
  
    // Select the memory card
    existingCard.classList.add('configurator__card--selected');
    const input = existingCard.querySelector('input[type="radio"]');
    if (input) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  
    // Update prices
    const section = memoryList.closest('.configurator__section');
    this.updatePriceDifferences(section);
  }

  getMemoryData(memoryId) {
    const memoryCard = this.querySelector(`#configurator-memory input[value="${memoryId}"]`)?.closest('.configurator__card');
    if (!memoryCard) return null;
  
    return {
      size: memoryCard.dataset.memory_size,
      speed: memoryCard.dataset.filter_memory_speed
    };
  }
  
  applyMemoryFilters(memoryData) {
    const section = this.querySelector('#configurator-memory').closest('.configurator__section');
    
    // Reset existing filters
    section.querySelectorAll('.configurator__filter-btn').forEach(btn => {
      btn.classList.remove('configurator__filter-btn--active');
    });
  
    // Apply speed filter if it exists
    if (memoryData.speed) {
      const speedButton = section.querySelector(
        `.configurator__filter-btn[data-filter-field="filter_memory_speed"][data-filter-value="${memoryData.speed}"]`
      );
      if (speedButton) speedButton.classList.add('configurator__filter-btn--active');
    }
  
    // Apply size filter if it exists
    if (memoryData.size) {
      const sizeSelect = section.querySelector('#memory-size-filter');
      if (sizeSelect) sizeSelect.value = memoryData.size;
    }
  
    // Use existing method to update card visibility
    this.updateMemoryCards();
  }

  selectPreBuiltRaid(raidId) {
    const raidSection = this.querySelector('#raid-controllers-section');
    if (!raidSection) return;
  
    // Find the raid card in any subsection
    const targetInput = raidSection.querySelector(`input[value="${raidId}"]`);
    if (!targetInput) return;
  
    const targetCard = targetInput.closest('.configurator__card');
    const subsection = targetCard.closest('.configurator__subsection');
    const raidType = subsection.querySelector('ul').dataset.raidType;
  
    // Clear selections in the specific subsection only
    subsection.querySelectorAll('.configurator__card').forEach(card => {
      card.classList.remove('configurator__card--selected');
      const input = card.querySelector('input[type="radio"]');
      if (input) input.checked = false;
    });
  
    // Select the new card
    targetCard.classList.add('configurator__card--selected');
    targetInput.checked = true;
    targetInput.dispatchEvent(new Event('change', { bubbles: true }));
  
    // Clear selections in other subsections if they exist
    raidSection.querySelectorAll('.configurator__subsection').forEach(otherSubsection => {
      if (otherSubsection !== subsection) {
        otherSubsection.querySelectorAll('.configurator__card').forEach(card => {
          card.classList.remove('configurator__card--selected');
          const input = card.querySelector('input[type="radio"]');
          if (input) input.checked = false;
        });
      }
    });
  
    // Update prices
    this.updatePriceDifferences(raidSection);
  }

  selectPreBuiltComponent(componentId, containerId) {
    const container = this.querySelector(`#${containerId}`);
    if (!container) return;
  
    // Find the target input
    const targetInput = container.querySelector(`input[value="${componentId}"]`);
    if (!targetInput) return;
  
    const targetCard = targetInput.closest('.configurator__card');
  
    // Clear existing selections
    container.querySelectorAll('.configurator__card').forEach(card => {
      card.classList.remove('configurator__card--selected');
      const input = card.querySelector('input[type="radio"]');
      if (input) input.checked = false;
    });
  
    // Select the new card
    targetCard.classList.add('configurator__card--selected');
    targetInput.checked = true;
    targetInput.dispatchEvent(new Event('change', { bubbles: true }));
  
    // Update prices
    const section = container.closest('.configurator__section');
    this.updatePriceDifferences(section);
  }

  selectPreBuiltOS(osId) {
    if (!osId) return;
  
    // Find all OS dropdowns and their wrappers
    const osWrappers = this.querySelectorAll('.configurator__dropdown-wrapper[data-os-type]');
    let targetWrapper = null;
    let targetDropdown = null;
  
    // Find which dropdown contains our OS option
    for (const wrapper of osWrappers) {
      const dropdown = wrapper.querySelector('select[name="os-selection"]');
      const hasOption = dropdown.querySelector(`option[value="${osId}"]`);
      if (hasOption) {
        targetWrapper = wrapper;
        targetDropdown = dropdown;
        break;
      }
    }
  
    if (!targetWrapper || !targetDropdown) {
      console.warn('OS dropdown or wrapper not found for ID:', osId);
      return;
    }
  
    // First, reset all other OS dropdowns
    osWrappers.forEach(wrapper => {
      if (wrapper !== targetWrapper) {
        const otherDropdown = wrapper.querySelector('select[name="os-selection"]');
        if (otherDropdown) {
          otherDropdown.value = '';
        }
      }
    });
  
    // Get the OS type and find the corresponding filter button
    const osType = targetWrapper.dataset.osType;
    const filterBtn = this.querySelector(`.configurator__filter-btn[data-filter-field="filter_operation_system"][data-filter-value="${osType}"]`);
  
    // Click the filter button to show the correct dropdown
    if (filterBtn) {
      filterBtn.click();
    }
  
    // Set the value
    targetDropdown.value = osId;
  
    // Important: First trigger change to update price differences
    targetDropdown.dispatchEvent(new Event('change', { 
      bubbles: true,
      cancelable: true 
    }));
  
    // Handle error message
    const errorMessage = targetDropdown.closest('.configurator__section')?.querySelector('.configurator__error-message');
    if (errorMessage) {
      errorMessage.style.display = 'none';
    }
  }

  // ************* Money Related Methods *************

  updatePriceDifferences(section) {
    if (!section) return;
    section.querySelectorAll('ul').forEach(ul => {
      const selectedInput = ul.querySelector('input[type="radio"]:checked');
      if (!selectedInput) return;

      const selectedCard = selectedInput.closest('.configurator__card');
      const selectedPrice = parseFloat(selectedCard.querySelector('.configurator__card-price').dataset.price);
      const selectorName = selectedInput.name;

      // Special handling for RAID controllers
      if (section.id === 'raid-controllers-section') {
        // Get all RAID cards across all subsections
        section.querySelectorAll('.configurator__card').forEach(card => {
          const priceElement = card.querySelector('.configurator__card-price');
          
          if (card === selectedCard) {
            if (!priceElement.dataset.included) {
              priceElement.textContent = ''; 
            }
            return;
          }
          
          if (priceElement.dataset.included === 'true') {
            return;
          }

          const cardPrice = parseFloat(priceElement.dataset.price);
          const priceDifference = cardPrice - selectedPrice;
          priceElement.textContent = priceDifference === 0 ? 
            '+$0.00' : 
            (priceDifference > 0 ? '+' : '') + this.formatMoney(priceDifference);
        });
      } else{
        // Regular price difference calculation for other sections
        ul.querySelectorAll(`input[name="${selectorName}"]`).forEach(input => {
          const card = input.closest('.configurator__card');
          const priceElement = card.querySelector('.configurator__card-price');
          
          if (card === selectedCard) {
            if (!priceElement.dataset.included) {
              priceElement.textContent = '';
            }
            return;
          }
          if (priceElement.dataset.included === 'true') {
            return;
          }

          const cardPrice = parseFloat(priceElement.dataset.price);
          const priceDifference = cardPrice - selectedPrice;
          priceElement.textContent = priceDifference === 0 ? 
            '+$0.00' : 
            (priceDifference > 0 ? '+' : '') + this.formatMoney(priceDifference);
        });
      }
    });
}
  
  setupPriceUpdates() {
    const summaryPriceElement = this.querySelector('.configurator__total-price');
    // Try to get base price from multiple possible sources
    const basePrice = parseFloat(this.dataset.basePrice) || 
                      parseFloat(summaryPriceElement?.dataset.basePrice) || 
                      0;
  
    // Listen for configuration changes from modal selections
    this.addEventListener('configuration-change', () => {
      this.updateTotalPrice(basePrice);
      this.updateTotalConsumption();
      this.updateQuoteModalPrice();
      this.updateQuoteTextArea();
    });

   
    // Initial price update
    this.updateTotalPrice(basePrice);
    this.updateQuoteTextArea();
  }

  updateTotalConsumption() {
    const summaryWattsElement = document.querySelector('.product-summary-watts');
    const summaryConsumptionElement = document.querySelectorAll('.product-summary-watts');
    const baseConsumption = parseFloat(summaryWattsElement?.dataset.baseConsumption || '0');

    let totalConsumption = baseConsumption;

    this.querySelectorAll('.configurator__card--selected [data-consumption]').forEach(consumptionElement => {
      totalConsumption += parseFloat(consumptionElement.dataset.consumption || '0');
    });

    let driveConsumption = this.getSelectedDriveConsumption();
    totalConsumption += driveConsumption;

    if (summaryConsumptionElement) {
      summaryConsumptionElement.forEach(element => {
        element.innerHTML = `${Math.round(totalConsumption)}W`;
      });
    }

    const powerSupplyCards = this.querySelectorAll('#configurator-power-supplies [data-max-watts]');
    if (powerSupplyCards) {
      powerSupplyCards.forEach(powerSupplyCard => {
        const maxWatts = parseFloat(powerSupplyCard.dataset.maxWatts);
      
      if (totalConsumption > maxWatts) {
        this.updatePowerSupplyStatus(powerSupplyCard, totalConsumption, maxWatts);
        } else {
          this.updatePowerSupplyStatus(powerSupplyCard, totalConsumption, maxWatts);
        }
      });
    }
  }

  updatePowerSupplyStatus(powerSupplyCard, totalConsumption, maxWatts) {
    const card = powerSupplyCard.closest('.configurator__card');
    const input = card.querySelector('input[type="radio"]');
    const section = card.closest('.configurator__section');
    const errorMessage = section.querySelector('.configurator__error-message') || this.createPowerSupplyErrorMessage(section);
    
    if (totalConsumption > maxWatts) {
      card.classList.add('configurator__card--over-max-watts');
      input.disabled = true;
      
      // If this card was selected, unselect it
      if (input.checked) {
        input.checked = false;
        card.classList.remove('configurator__card--selected');
        errorMessage.textContent = `This power supply cannot support the total power consumption of ${totalConsumption}W. Please select a higher wattage power supply.`;
        errorMessage.style.display = 'block';
        this.dispatchEvent(new CustomEvent('configuration-change'));
      }
    } else {
      card.classList.remove('configurator__card--over-max-watts');
      input.disabled = false;
      errorMessage.style.display = 'none';
    }
  }
  
  createPowerSupplyErrorMessage(section) {
    const errorMessage = document.createElement('div');
    errorMessage.classList.add('configurator__error-message');
    errorMessage.style.color = 'red';
    section.querySelector('.configurator__content-wrapper').appendChild(errorMessage);
    return errorMessage;
  }

  updateTotalPrice(basePrice) {
    const summaryPriceElement = this.querySelector('.configurator__total-price');    
    let total = basePrice || 0;

    // Calculate base total without warranty
    total += this.getSelectedDrivePrices();

    this.querySelectorAll('.configurator__card--selected .configurator__card-price').forEach(priceElement => {
      const card = priceElement.closest('.configurator__card');
      // Skip drives section and warranty section
      if (!card.closest('#configurator-drives') && !card.closest('.configurator__warranty-card')) {
        total += parseFloat(priceElement.dataset.price || '0');
      }
    });

    // Add Network Card selection price
    const networkSelect = this.querySelector('.configurator__dropdown[name="network-card-selection"]');
    if (networkSelect && networkSelect.value) {
      const selectedNetworkOption = networkSelect.options[networkSelect.selectedIndex];
      const networkPrice = parseFloat(selectedNetworkOption.dataset.price || '0');
      if (!isNaN(networkPrice)) {
        total += networkPrice;
      }
    } 

   // Check all OS selects and find the one with a selected value
   const osSelects = this.querySelectorAll('.configurator__dropdown[name="os-selection"]');
   for (const osSelect of osSelects) {
     const selectedOsOption = osSelect.options[osSelect.selectedIndex];
     if (selectedOsOption && selectedOsOption.value !== '') {  
       const osPrice = parseFloat(selectedOsOption.dataset.price || '0');
       if (!isNaN(osPrice)) {
         total += osPrice;
         break; // Only one OS can be selected at a time
       }
     }
   }
    // Add prices from selected add-ons
    this.querySelectorAll('.configurator__add-on-select-btn--selected').forEach(button => {
      const price = parseFloat(button.dataset.price || '0');
      if (!isNaN(price)) {
        total += price;
      }
    });

    const startPriceElement = this.querySelector('#configurator-start-price');
    if (startPriceElement && this.isFirstUpdate) {
      startPriceElement.textContent = this.formatMoney(total);
      this.isFirstUpdate = false;
    }

    // Calculate warranty price based on the total WITHOUT warranty
    this.updateWarrantyPrices(total);
    
    // Add warranty price to final total if SMS warranty is selected
    const selectedWarrantyCard = this.querySelector('.configurator__warranty-card input[name="warranty-selection"]:checked');
    if (selectedWarrantyCard?.value === 'sms-warranty') {
      const warrantyPrice = parseFloat(selectedWarrantyCard.dataset.price || '0');
      total += warrantyPrice;
    }

    try {
      if (summaryPriceElement) {
        summaryPriceElement.innerHTML = this.formatMoney(total);
      }
      const fixedSummaryPrice = document.querySelectorAll('.product-summary-price');
      if (fixedSummaryPrice) {
        fixedSummaryPrice.forEach(price => {
          price.innerHTML = this.formatMoney(total);
        });
      }

    } catch (error) {
      console.error('Error updating prices:', error);
    }
  }

  setupWarrantyHandlers() {
    const warrantyDataElement = document.getElementById('warranty-data');
    const warrantyData = JSON.parse(warrantyDataElement.textContent);
  
    const warrantySelect = this.querySelector('select[name="warranty-years"]');
    if (warrantySelect) {
      warrantySelect.addEventListener('change', (event) => {
        const selectedIndex = event.target.selectedIndex;
        const selectedWarranty = warrantyData.warranties[selectedIndex];
        const card = event.target.closest('.configurator__warranty-card');
        
        // Update title
        const title = card.querySelector('.configurator__card-title--warranty');
        title.textContent = selectedWarranty.title || title.dataset.defaultTitle;
        
        // Update description
        const description = card.querySelector('.configurator__card-description');
        description.textContent = selectedWarranty.description;
        
        // Update includes section
        const includesSection = card.querySelector('.configurator__warranty-features');
        
        // First decode the HTML string
        const decodedHtml = this.decodeHtml(selectedWarranty.includes);
        // Parse the decoded HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(decodedHtml, 'text/html');
        // Get the content
        const richTextContent = doc.querySelector('.metafield-rich_text_field');

        if (richTextContent) {
          includesSection.innerHTML = richTextContent.innerHTML;
        } else {
          // Fallback: try using the decoded HTML directly
          includesSection.innerHTML = decodedHtml;
        }

        // Update price calculation based on percentage
        const priceElement = card.querySelector('.configurator__card-price--warranty');
        const basePrice = parseFloat(priceElement.dataset.basePrice) || 0;
        const warrantyPrice = (basePrice * (selectedWarranty.percent / 100));
        priceElement.dataset.price = warrantyPrice;
        priceElement.textContent = `+${this.formatMoney(warrantyPrice)}`;

        // Trigger price update
        this.dispatchEvent(new CustomEvent('configuration-change'));
      });

      // Trigger change event to initialize the first selection
      warrantySelect.dispatchEvent(new Event('change'));
    }
  }

  // Helper method to decode HTML
  decodeHtml(html) {
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  }

  updateWarrantyPrices(baseTotal) {
    const warrantyCard = this.querySelector('#configurator__warranty-card');
    const warrantySelect = warrantyCard?.querySelector('select[name="warranty-years"]');
    const warrantyRadio = warrantyCard?.querySelector('input[name="warranty-selection"][value="sms-warranty"]');
    const warrantyPriceElement = warrantyCard?.querySelector('.configurator__card-price');

    if (!warrantySelect || !warrantyRadio || !warrantyPriceElement) return;

    const selectedPercentage = parseFloat(warrantySelect.value || '0');
    const warrantyPrice = baseTotal * (selectedPercentage / 100);

    warrantyPriceElement.textContent = `+${this.formatMoney(warrantyPrice)}`;
    warrantyPriceElement.dataset.price = warrantyPrice;
    warrantyRadio.dataset.price = warrantyPrice;
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
      }).format(cents/100);
    } catch (error) {
      console.error('Error formatting money:', error);
      return '$0.00';
    }
  }

  getConfiguration() {
    // Get data from server-configurator element
    const configurator = this.closest('server-configurator');
    const productTitle = `${configurator?.dataset.productTitle}-configured` || 'Server Configuration';
    const baseProductId = configurator?.dataset.productId;
    const mainProductImage = configurator?.dataset.productImageUrl;
    // Initialize array to store all variant IDs with their details
    const variantIds = [];
    
    // Add base product first if it exists
    if (baseProductId) {
      variantIds.push({
        id: this.formatGlobalId(baseProductId),
        quantity: 1,
        component: 'Server',
      });
    }
  
    // Get all selected components and their variant IDs
    const selectedComponents = {
      processor: this.getSelectedComponentInfo('processor-selection', 'Processor') || { default: 'None Selected', component: 'Processor' },
      heatsink: this.getSelectedComponentInfo('heatsink-selection', 'Heatsink') || { default: 'None Selected', component: 'Heatsink' },
      memory: this.getSelectedComponentInfo('memory-selection', 'Memory') || { default: 'None Selected', component: 'Memory' },
      raid: this.getSelectedComponentInfo('raid-selection', 'RAID Controller') || { default: 'None Selected', component: 'Raid' },
      remote: this.getSelectedComponentInfo('remote-selection', 'Remote Access') || { default: 'None Selected', component: 'remote' },
      power: this.getSelectedComponentInfo('power-supply-selection', 'Power Supply') || { default: 'None Selected', component: 'Power Supply' },
      network: this.getSelectedComponentInfo('network-card-selection', 'Network Card') || { default: 'None Selected', component: 'Network Card' },
      os: this.getOsSelection() || { default: 'None Selected', component: 'Operating System' },
      warranty: this.getSelectedWarranty(),
      drives: Array.from(this.selectedDrives.entries()).map(([driveId, quantity]) => {
        const input = this.querySelector(`input[data-drive-id="${driveId}"]`);
        const card = input?.closest('.configurator__card');
        const variantId = this.getVariantId(card);
        const title = card.querySelector('.configurator__card-title')?.textContent;

        if (variantId) {
          return {
            variantId: variantId,
            quantity: quantity,
            component: 'Storage Drive'
          };
        } else {
          return {
            default: title || 'None Selected',
            component: 'Storage Drive'
          };
        }
      }),
      addOns: Array.from(this.querySelectorAll('.configurator__add-on-select-btn--selected')).map(button => {
        const title = button.closest('.configurator__add-on-card')?.querySelector('.configurator__add-on-title')?.textContent;
        const dropdown = button.closest('.configurator__add-on-content')?.querySelector('.configurator__dropdown');
        const selectedOption = dropdown?.options[dropdown.selectedIndex];
        
        return {
          variantId: selectedOption?.dataset.variantId || button.dataset.variantId,
          quantity: 1,
          component: title || 'Add-on'
        };
      }),
    };
  
    // Add all valid component variants to the array
    Object.entries(selectedComponents).forEach(([type, component]) => {
      if (Array.isArray(component)) {
        if (component.length === 0) {
          variantIds.push({
            default: 'None Selected',
            component: type
          });
        } 
        else {
          component.forEach(item => {
            if (item.variantId) {
              variantIds.push({
                id: this.formatGlobalId(item.variantId),
                quantity: item.quantity,
                component: item.component,
              });
            } else if (item.default) {
              variantIds.push({
                default: item.default,
                component: item.component
              });
            }
          });
        }
      } else if (component) {
        if (component.component === 'Warranty') {
          variantIds.push(component);
        } else if (component.variantId) {
          variantIds.push({
            id: this.formatGlobalId(component.variantId),
            quantity: 1,
            component: component.component
          });
        } else if (component.default) {
          variantIds.push({
            default: component.default,
            component: component.component
          });
        }
      }
    });
  
    return {
      title: productTitle,
      components: variantIds,
      imageFile: mainProductImage
    };
  }

  getSelectedWarranty() {
    const selectedWarrantyRadio = this.querySelector('.configurator__warranty-card input[name="warranty-selection"]:checked');
    const warrantyCard = selectedWarrantyRadio?.closest('.configurator__card');
    
    if (selectedWarrantyRadio.value === 'sms-warranty') {
        const warrantySelect = this.querySelector('select[name="warranty-years"]');
        const selectedYear = warrantySelect?.options[warrantySelect.selectedIndex]?.textContent;
        const priceElement = warrantyCard.querySelector('.configurator__card-price');
        
        return {
            component: 'Warranty',
            title:`SMS+ Warranty (${selectedYear?.trim()||'1 Year'})`,
            price: parseFloat(priceElement?.dataset.price || '0')
        };
    } else {
        return {
            component: 'Warranty',
            title: '180 day limited warranty',
            price: 0
        };
    }
  }
  
  
  // Helper methods to get component info
  getSelectedComponentInfo(selectorName, componentType) {
    const selected = this.querySelector(`input[name="${selectorName}"]:checked`);
    if (!selected) return null;
  
    const card = selected.closest('.configurator__card');
    if (!card) return null;
  
    const variantId = this.getVariantId(card);
    const title = card.querySelector('.configurator__card-title')?.textContent;
  
    if (variantId) {
      return {
        variantId: variantId,
        component: componentType
      };
    } else {
      return {
        default: title,
        component: componentType
      };
    }
  }
  
  getNetworkSelection() {
    const selected = this.querySelector('input[name="network-card-selection"]:checked');
    if (!selected) return null;
  
    const card = selected.closest('.configurator__card');
    if (!card) return null;
  
    // Handle "None" option
    if (selected.value === 'none') {
      return {
        default: 'None',
        component: 'Network Card'
      };
    }
  
    const variantId = selected.dataset.variantId;
    const title = card.querySelector('.configurator__card-title')?.textContent;
  
    if (variantId) {
      return {
        variantId: variantId,
        component: 'Network Card'
      };
    } else {
      return {
        default: title || 'None',
        component: 'Network Card'
      };
    }
  }
  
  getOsSelection() {
    const osSelects = this.querySelectorAll('.configurator__dropdown[name="os-selection"]');
    
    for (const osSelect of osSelects) {
      const selectedOption = osSelect.options[osSelect.selectedIndex];
      if (selectedOption && selectedOption.value !== '') {
        const variantId = selectedOption.dataset.variantId || selectedOption.value;
      
        if (variantId) {
          return {
            variantId: variantId,
            component: 'Operating System'
          };
        } else {
          return {
            default: selectedOption.text,
            component: 'Operating System'
          };
        }
      }
    }
    
    return null;
  }

  // Helper method to format variant IDs as Shopify Global IDs
  formatGlobalId(variantId) {
    return `gid://shopify/ProductVariant/${variantId}`;
  }

  getSelectedPrice(radio) {
    const card = radio.closest('.configurator__card');
    const priceElement = card?.querySelector('.configurator__card-price');
    return priceElement ? parseFloat(priceElement.dataset.price) : 0;
  }

  // Get the selected title and price for a given selector name
  getSelectedTitle(selectorName) {
    const selected = this.querySelector(`input[name="${selectorName}"]:checked`);
    if (!selected) return null;
    
    const card = selected.closest('.configurator__card');
    if (!card) return null;

    const title = card.querySelector('.configurator__card-title')?.textContent;
    const price = card.querySelector('.configurator__card-price')?.dataset.price;

    return title ? (DISPLAY_LINE_ITEM_PRICE ? `${title} (+${this.formatMoney(parseFloat(price))})` : title) : null;
  }
  
  getVariantId(element) {
    // First try to get variant ID from data attribute
    const variantId = element?.dataset.variantId || 
                      element?.querySelector('[data-variant-id]')?.dataset.variantId;
    
    if (variantId) return parseInt(variantId);
    
    // If no variant ID found, try to get it from the product form
    const productForm = element?.closest('product-form');
    if (productForm) {
      const variantSelect = productForm.querySelector('select[name="id"]');
      if (variantSelect) return parseInt(variantSelect.value);
    }
    
    return null;
  }


  getSelectedOsTitle() {
    const osSelect = this.querySelector('#configurator-os');
    if (!osSelect || !osSelect.value) return null;
    
    const selectedOption = osSelect.options[osSelect.selectedIndex];
    return selectedOption.text;
  }

  setupAddOns() {
    const addOnSelects = this.querySelectorAll('.configurator__dropdown[data-filter-type="add-on"]');
  
    addOnSelects.forEach(select => {
      const options = select.querySelectorAll('option:not([value=""])');
      const wrapper = select.closest('.configurator__dropdown-wrapper');
      const contentDiv = select.closest('.configurator__add-on-content');
      const priceElement = contentDiv.querySelector('.configurator__add-on-price');
      const selectButton = contentDiv.querySelector('.configurator__add-on-select-btn');
      
      if (options.length === 1) {
        wrapper.style.display = 'none';
      } else {
        priceElement.style.display = 'none';
      }

      // Update button attributes data-price & data-variant-id
      const updateButtonAttributes = () => {
        let selectedOption = select.options[select.selectedIndex];

        if (selectedOption) {
            // Update data-price
            if (selectedOption.dataset.price) {
                selectButton.setAttribute('data-price', selectedOption.dataset.price);
            } else {
                selectButton.removeAttribute('data-price');
            }

            // Update data-variant-id
            if (selectedOption.dataset.variantId) {
                selectButton.setAttribute('data-variant-id', selectedOption.dataset.variantId);
            } else {
                selectButton.removeAttribute('data-variant-id');
            }
        }
    };
  
      // Add click handler for the select button
      selectButton.addEventListener('click', () => {
        // If already selected, unselect
        if (selectButton.classList.contains('configurator__add-on-select-btn--selected')) {
          // Reset button state
          selectButton.textContent = 'Select';
          selectButton.disabled = false;
          selectButton.classList.remove('configurator__add-on-select-btn--selected');
          
          // Reset dropdown if multiple options
          if (options.length > 1) {
            select.selectedIndex = 0;
          }
  
          // Trigger configuration change event
          this.dispatchEvent(new CustomEvent('configuration-change'));
          return;
        }
  
        // Handle selection
        let selectedOption;
  
        if (options.length === 1) {
          // Single option case
          selectedOption = options[0];
        } else {
          selectedOption = select.options[select.selectedIndex];
          
          if (!selectedOption.value) {
            alert('Please select an option before continuing');
            return;
          }
        }
  
        // Update button state
        selectButton.textContent = 'Selected';
        selectButton.classList.add('configurator__add-on-select-btn--selected');
  
        this.dispatchEvent(new CustomEvent('configuration-change'));
      });
  
      if (options.length > 1) {
        select.addEventListener('change', () => {
          if (selectButton.classList.contains('configurator__add-on-select-btn--selected')) {
            selectButton.textContent = 'Select';
            selectButton.disabled = false;
            selectButton.classList.remove('configurator__add-on-select-btn--selected');
            
            this.dispatchEvent(new CustomEvent('configuration-change'));
          }

          // Update button attributes on change
          updateButtonAttributes();
        });
      }
    });
  }

  // Cart Related Methods
  setupAddToCart() {
    const addToCartButton = document.querySelector('.product-summary-add-to-cart');
    if (addToCartButton) {
      addToCartButton.addEventListener('click', () => this.addToCart());
    }
  }

  setupRequestQuote() {
    const requestQuoteButton = document.querySelector('.configurator__quote-btn');
    if (requestQuoteButton) {
      requestQuoteButton.addEventListener('click', () => this.requestQuote());
    }
  }

  async sendConfiguration() {
    try {
      const configuration = this.getConfiguration();

      const response = await fetch('https://save-my-server-legacy.gadget.app/createProduct', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Add any additional headers if needed
        },
        body: JSON.stringify(configuration)
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const data = await response.json();
      return data;
      
    } catch (error) {
      console.error('Error sending configuration:', error);
      throw error;
    }
  }

  getConfigurationProperties() {
    const properties = {};
    
    // Add processor
    if (this.getSelectedComponentInfo('processor-selection', 'processor')) {
      properties['Processor'] = this.getSelectedTitle('processor-selection');
    }
 
    // Add heatsink
    if (this.getSelectedComponentInfo('heatsink-selection', 'heatsink')) {
      properties['Heatsink'] = this.getSelectedTitle('heatsink-selection');
    }
  
    // Add memory
    if (this.getSelectedComponentInfo('memory-selection', 'memory')) {
      properties['Memory'] = this.getSelectedTitle('memory-selection');
    }
  
    // Add RAID
    if (this.getSelectedComponentInfo('raid-selection', 'raid')) {
      properties['RAID Controller'] = this.getSelectedTitle('raid-selection');
    }

     // Add drives
     const driveProperties = [];
     this.selectedDrives.forEach((quantity, driveId) => {
       const input = this.querySelector(`input[data-drive-id="${driveId}"]`);
       const card = input?.closest('.configurator__card');
       const title = card?.querySelector('.configurator__card-title')?.textContent;
       if (title) {
         if (quantity > 1) {
           driveProperties.push(`${title} (Qty: ${quantity})`);
         } else {
           driveProperties.push(title);
         }
       }
     });
   
     if (driveProperties.length > 0) {
       properties['Storage Drives'] = driveProperties.join(', ');
     }
  
    // Add remote
    if (this.getSelectedComponentInfo('remote-selection', 'remote')) {
      properties['Remote Access'] = this.getSelectedTitle('remote-selection');
    }

    // Add power
    if (this.getSelectedComponentInfo('power-supply-selection', 'power')) {
      properties['Power Supply'] = this.getSelectedTitle('power-supply-selection');
    }
    
    // Add network card
    if (this.getSelectedComponentInfo('network-card-selection', 'network')) {
      properties['Network Card'] = this.getSelectedTitle('network-card-selection');
    }
    // Add OS
    const osSelects = this.querySelectorAll('.configurator__dropdown[name="os-selection"]');
    for (const osSelect of osSelects) {
      const selectedOption = osSelect.options[osSelect.selectedIndex];
      if (selectedOption && selectedOption.value !== '') {
        properties['Operating System'] = selectedOption.text;
        break; 
      }
    }
    
    // Add Warranty
    const selectedWarranty = this.querySelector('.configurator__warranty-card input[name="warranty-selection"]:checked');
    if (selectedWarranty) {      
      const warrantyCard = selectedWarranty.closest('.configurator__warranty-card');
      const warrantyTitle = warrantyCard?.querySelector('.configurator__card-title--warranty')?.textContent;
      if (warrantyTitle) {
        properties['Warranty'] = warrantyTitle;
      }
    }
  
    // Add Add-ons
    const selectedAddOns = Array.from(this.querySelectorAll('.configurator__add-on-select-btn--selected'));
    if (selectedAddOns.length > 0) {
      const addOnProperties = selectedAddOns.map(button => {
        const card = button.closest('.configurator__add-on-card');
        const title = card?.querySelector('.configurator__add-on-title')?.textContent;
        const dropdown = card?.querySelector('.configurator__dropdown');
        const selectedOption = dropdown?.options[dropdown.selectedIndex];
        
        if (selectedOption && selectedOption.value) {
          return `${title}: ${selectedOption.text}`;
        }
        return title;
      });
      properties['Add-ons'] = addOnProperties.join(', ');
    }

    // Collect all variant IDs for hidden _components property (only if USE_BUNDLES is enabled)
    if (USE_BUNDLES) {
      const variantIds = [];

      // Get base product variant ID
      const configurator = this.closest('server-configurator');
      const baseProductId = configurator?.dataset.productId;
      if (baseProductId) {
        variantIds.push(baseProductId);
      }

      // Get processor variant ID
      const processorInfo = this.getSelectedComponentInfo('processor-selection', 'processor');
      if (processorInfo?.variantId) {
        variantIds.push(processorInfo.variantId.toString());
      }

      // Get heatsink variant ID
      const heatsinkInfo = this.getSelectedComponentInfo('heatsink-selection', 'heatsink');
      if (heatsinkInfo?.variantId) {
        variantIds.push(heatsinkInfo.variantId.toString());
      }

      // Get memory variant ID
      const memoryInfo = this.getSelectedComponentInfo('memory-selection', 'memory');
      if (memoryInfo?.variantId) {
        variantIds.push(memoryInfo.variantId.toString());
      }

      // Get RAID variant ID
      const raidInfo = this.getSelectedComponentInfo('raid-selection', 'raid');
      if (raidInfo?.variantId) {
        variantIds.push(raidInfo.variantId.toString());
      }

      // Get drive variant IDs
      this.selectedDrives.forEach((quantity, driveId) => {
        const input = this.querySelector(`input[data-drive-id="${driveId}"]`);
        const card = input?.closest('.configurator__card');
        const variantId = this.getVariantId(card);
        if (variantId) {
          variantIds.push(variantId.toString());
        }
      });

      // Get remote variant ID
      const remoteInfo = this.getSelectedComponentInfo('remote-selection', 'remote');
      if (remoteInfo?.variantId) {
        variantIds.push(remoteInfo.variantId.toString());
      }

      // Get power supply variant ID
      const powerInfo = this.getSelectedComponentInfo('power-supply-selection', 'power');
      if (powerInfo?.variantId) {
        variantIds.push(powerInfo.variantId.toString());
      }

      // Get network card variant ID
      const networkInfo = this.getSelectedComponentInfo('network-card-selection', 'network');
      if (networkInfo?.variantId) {
        variantIds.push(networkInfo.variantId.toString());
      }

      // Get OS variant ID
      const osSelectsForVariant = this.querySelectorAll('.configurator__dropdown[name="os-selection"]');
      for (const osSelect of osSelectsForVariant) {
        const selectedOption = osSelect.options[osSelect.selectedIndex];
        if (selectedOption && selectedOption.value !== '' && selectedOption.dataset.variantId) {
          variantIds.push(selectedOption.dataset.variantId);
          break;
        }
      }

      // Get add-on variant IDs
      selectedAddOns.forEach(button => {
        const dropdown = button.closest('.configurator__add-on-content')?.querySelector('.configurator__dropdown');
        const selectedOption = dropdown?.options[dropdown.selectedIndex];
        const variantId = selectedOption?.dataset.variantId || button.dataset.variantId;
        if (variantId) {
          variantIds.push(variantId);
        }
      });

      // Get warranty variant ID if SMS+ warranty is selected
      const selectedWarrantyRadio = this.querySelector(
        '.configurator__warranty-card input[name="warranty-selection"]:checked'
      );
      if (selectedWarrantyRadio?.value === 'sms-warranty') {
        const warrantySelect = this.querySelector('select[name="warranty-years"]');
        if (warrantySelect) {
          const selectedOption = warrantySelect.options[warrantySelect.selectedIndex];
          const selectedText = selectedOption?.textContent?.trim();

          // Map warranty years to variant IDs
          const warrantyVariantMap = {
            '1 Year': '47229670129919',
            '2 Years': '47229670162687',
            '3 Years': '47229670195455'
          };

          const warrantyVariantId = warrantyVariantMap[selectedText];
          if (warrantyVariantId) {
            variantIds.push(warrantyVariantId);
          }
        }
      }

      // Add hidden _components property with comma-separated variant IDs
      if (variantIds.length > 0) {
        properties['_components'] = variantIds.join(',');
      }
    }

    return properties;
  }
  
  // Utility function to create/manage loading spinner
  createLoadingButton(button, alternateColor = false) {
    const existingText = button.textContent.trim();
    button.textContent = '';

    const buttonText = document.createElement('span');
    buttonText.className = 'button-text';
    buttonText.textContent = existingText;
    
    const spinner = document.createElement('span');
    spinner.className = `loading-spinner hidden ${alternateColor ? 'loading-spinner--alternate' : ''}`;
    spinner.innerHTML = `
      <svg aria-hidden="true" class="icon icon-spinner" viewBox="0 0 20 20">
        <path d="M7.229 1.173a9.25 9.25 0 1 0 11.655 11.412 1.25 1.25 0 1 0-2.4-.698 6.75 6.75 0 1 1-8.506-8.329 1.25 1.25 0 1 0-.75-2.385z"/>
      </svg>
    `;
    
    button.appendChild(buttonText);
    button.appendChild(spinner);

    return {
      startLoading: () => {
        button.setAttribute('aria-busy', 'true');
        button.classList.add('button--loading');
        button.disabled = true;
        spinner.classList.remove('hidden');
      },
      stopLoading: () => {
        button.removeAttribute('aria-busy');
        button.disabled = false;
        button.classList.remove('button--loading');
        spinner.classList.add('hidden');
      }
    };
  }

  async addToCart() {
    // Find the button
    const button = document.querySelector('[data-add-to-cart-configurator]');
    if (!button) {
      console.error('Add to cart button not found');
      return;
    }

    // Initialize loading button
    const loadingButton = this.createLoadingButton(button);
    
    try {
      // Start loading state
      loadingButton.startLoading();
      
      // Get configuration and create product
      const configResponse = await this.sendConfiguration();

      if (!configResponse || !configResponse.variantId) {
        throw new Error('Invalid configuration response');
      }

      const { variantId, total } = configResponse;
      
      // Extract the numeric ID from the gid URL
      const matches = variantId.match(/ProductVariant\/(\d+)/);
      const variantNumericId = matches ? matches[1] : null;
      
      if (!variantNumericId) {
        throw new Error('Invalid variant ID format');
      }

      const properties = this.getConfigurationProperties();

      const formData = {
        items: [{
          id: variantNumericId,
          quantity: 1,
          properties: {
            _total_price: total,
            ...properties
          }
        }]
      };

      console.log(formData);

      const maxRetries = 10;
      const retryDelay = 2000;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        const response = await fetch(`${window.location.origin}/cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(formData)
        });

        const responseData = await response.json();

        if (response.ok) {
          break;
        }

        if (response.status === 422 && attempt < maxRetries) {
          console.warn(`Cart add attempt ${attempt} failed (422), retrying in ${retryDelay / 1000}s...`, responseData);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          continue;
        }

        console.error('Cart Error Response:', responseData);
        throw new Error(responseData.description || 'Error adding to cart');
      }
    } catch (error) {
      console.error('Detailed Error:', error);
      alert(`Failed to add item to cart: ${error.message}`);
    } finally {
      // Reset button state
      loadingButton.stopLoading();
      window.location.href = window.location.origin + '/cart';
    }
  }

  validateSelections() {
    const requiredSections = this.querySelectorAll('.configurator__grid[data-required="true"]');
    let valid = true;

    requiredSections.forEach(section => {
      const isSelected = section.querySelector('input[type="radio"]:checked');
      const detailsElement = section.closest('details'); // Get the parent details element
      const errorMessage = section.nextElementSibling; // Get the error message

      if (!isSelected) {
        valid = false; // If any required section has no selection, mark as invalid
        detailsElement.style.border = '2px solid red'; // Add red border to the details element
        errorMessage.style.display = 'block'; // Show error message
      } else {
        // If a selection is made, remove the red border and hide the error message
        detailsElement.style.border = ''; // Remove red border if valid
        errorMessage.style.display = 'none'; // Hide error message
      }
    });

    return valid; // Return true if all required sections have at least one selected
  }

  // ************* Aniamtions and Styling related Methods *************
  setupDetailsAnimation() {
    this.querySelectorAll('.configurator__section').forEach(section => {
      const content = section.querySelector('.configurator__content-wrapper');
      let isAnimating = false;

      // Handle clicks on summary
      section.addEventListener('click', (event) => {
        if (!event.target.closest('summary')) return;
        
        event.preventDefault();
        if (isAnimating) return;
        
        isAnimating = true;
        
        if (!section.open) {
          // Opening
          section.open = true;
          content.style.height = '0px';
          
          requestAnimationFrame(() => {
            content.style.height = content.scrollHeight + 'px';
            content.style.opacity = '1';
          });
        } else {
          // Closing
          content.style.height = content.scrollHeight + 'px';
          content.offsetHeight; // Force reflow
          
          requestAnimationFrame(() => {
            content.style.height = '0px';
            content.style.opacity = '0';
          });
        }
      });

      // Handle filter clicks with improved height adjustment
      section.querySelectorAll('.configurator__filter-btn').forEach(button => {
        button.addEventListener('click', () => {
          if (section.open) {
            // Set height to auto temporarily to get the true scroll height
            content.style.height = 'auto';
            const newHeight = content.scrollHeight;
            
            // Set back to the current height and then animate to new height
            content.style.height = content.scrollHeight + 'px';
            
            requestAnimationFrame(() => {
              content.style.height = newHeight + 'px';
            });
          }
        });
      });

      content.addEventListener('transitionend', (event) => {
        if (event.propertyName === 'height') {
          isAnimating = false;
          if (content.style.height === '0px') {
            section.open = false;
          } else {
            content.style.height = 'auto';
          }
        }
      });
    });
  }

  async requestQuote() {
    // Find the button
    const button = document.querySelector('[data-quote-configurator]');
    if (!button) {
      console.error('Quote button not found');
      return;
    }
  
    let loadingButton = null;  // Initialize loadingButton variable

    try {
      // Initialize loading button
      loadingButton = this.createLoadingButton(button, true);
      loadingButton.startLoading();
  
      // Get configuration and create product
      const configResponse = await this.sendConfiguration();
  
      if (!configResponse || !configResponse.variantId) {
        throw new Error('Unable to create configuration. Please try again or contact support.');
      }
  
      const { variantId, total } = configResponse;
      const properties = this.getConfigurationProperties();

      // Get the product title and image
      const configurator = this.closest('server-configurator');
      const productTitle =
        `${configurator?.dataset.productTitle}-configured` || 'Server Configuration';
      let productImage = configurator?.dataset.productImageUrl || '';

      // Convert relative image URLs to full CDN URLs
      if (productImage && productImage.startsWith('files/')) {
        productImage = productImage.replace('files/', 'https://savemyserver.com/cdn/shop/files/');
      }

      // Prepare cart item
      const cartItem = {
        variantId: variantId,
        quantity: 1,
        title: productTitle,
        price: total,
        image: productImage,
        properties: properties,
        configuration: this.getConfiguration(),
      };

      // Stop loading before showing modal
      if (loadingButton) {
        loadingButton.stopLoading();
        loadingButton = null; // Prevent double-stop in finally block
      }

      // Get the quote threshold from settings (in dollars)
      const thresholdDollars = parseFloat(configurator?.dataset.quoteThreshold) || 500;
      const thresholdCents = thresholdDollars * 100; // Convert to cents

      // Check if item price is below threshold
      if (total < thresholdCents) {
        // Price is below threshold - add directly to quote cart without modal
        console.log(`Item price ($${total / 100}) is below threshold ($${thresholdDollars}). Adding directly to quote cart.`);

        // Add to quote cart using fallback method
        this.saveToDraftCartFallback(cartItem);

        // Dispatch event for icon update
        document.dispatchEvent(new CustomEvent('quote-cart-updated', {
          detail: { action: 'add', item: cartItem }
        }));

        // Show success dialog
        if (window.QuoteSuccessDialog && window.SMS_QUOTE_SETTINGS) {
          window.QuoteSuccessDialog.show({
            title: window.SMS_QUOTE_SETTINGS.addedTitle,
            message: window.SMS_QUOTE_SETTINGS.addedMessage,
            buttonText: 'View Quote Cart',
            buttonUrl: '/pages/quote-cart'
          });
        }

      } else {
        // Price is at or above threshold - show the quote modal
        console.log(`Item price ($${total / 100}) meets threshold ($${thresholdDollars}). Showing quote form.`);

        if (window.SMS_QuoteModal && window.SMS_QuoteModal.showForSingleItem) {
          window.SMS_QuoteModal.showForSingleItem(cartItem);
        } else {
          throw new Error('Quote modal system not loaded. Please refresh the page and try again.');
        }
      }


    } catch (error) {
      console.error('Quote Error:', error);
      
      // User-friendly error messages
      let userMessage = 'Failed to create quote: ';
      if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
        userMessage += 'Network connection error. Please check your internet connection and try again.';
      } else if (error.message.includes('timeout')) {
        userMessage += 'Request timed out. Please try again.';
      } else {
        userMessage += error.message;
      }
      
      alert(userMessage);
    } finally {
      // Only stop loading if the button was initialized and not already stopped
      if (loadingButton) {
        loadingButton.stopLoading();
      }
    }
  }

  // Fallback method to save directly to localStorage if web component is not available
  saveToDraftCartFallback(item) {
    const storageKey = 'sms_quote_cart';
    let cart;

    try {
      const cartData = localStorage.getItem(storageKey);
      cart = cartData ? JSON.parse(cartData) : { items: [], createdAt: new Date().toISOString() };
    } catch (error) {
      console.error('Error reading quote cart:', error);
      cart = { items: [], createdAt: new Date().toISOString() };
    }

    // Check if an identical item already exists
    const existingItem = cart.items.find(cartItem => {
      // Check if variant IDs match
      if (cartItem.variantId !== item.variantId) {
        return false;
      }

      // Check if properties match
      const props1 = cartItem.properties || {};
      const props2 = item.properties || {};

      const keys1 = Object.keys(props1).sort();
      const keys2 = Object.keys(props2).sort();

      // Different number of properties
      if (keys1.length !== keys2.length) {
        return false;
      }

      // Check each property value
      for (let key of keys1) {
        if (props1[key] !== props2[key]) {
          return false;
        }
      }

      return true;
    });

    if (existingItem) {
      // Item exists, increase quantity
      existingItem.quantity = (existingItem.quantity || 1) + (item.quantity || 1);
      console.log('Increased quantity for existing item (fallback):', existingItem);
    } else {
      // New item, add to cart
      const itemId = `item_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

      const newItem = {
        id: itemId,
        ...item,
        addedAt: new Date().toISOString(),
      };

      cart.items.push(newItem);
      console.log('Item saved to quote cart (fallback):', newItem);
    }

    try {
      localStorage.setItem(storageKey, JSON.stringify(cart));
    } catch (error) {
      console.error('Error saving to quote cart:', error);
      throw new Error('Unable to save to quote cart');
    }
  }

  resetConfigurator() {
    // Check if navigation is from back/forward button using modern API
    const navigation = performance.getEntriesByType('navigation');
    if (navigation.length > 0 && navigation[0].type === 'back_forward') {
      window.location.reload();
      return;
    }
  }
}

customElements.define('server-configurator', ServerConfigurator); 
